/**
 * OVERVOLT simulation systems.
 *
 * Every function here is a pure-ish mutator over {@link GameState}: it reads
 * config + RNG and mutates state. No wall-clock time, no `Math.random()` —
 * all randomness flows through the seeded {@link Rng}. One call to
 * {@link simTick} advances the world by exactly one logical tick.
 */
import {
  CONFIG,
  type AbilityDef,
  type AbilityId,
  type DifficultyDef,
  type EnemyType,
  type OverloadTier,
  type TargetMode,
  type TowerType,
  type WaveDef,
  type WaveGroup,
} from '../config';
import type { SoundName } from '../engine/audio';
import { Rng } from '../engine/rng';
import { clamp } from '../engine/util';
import {
  applyTowerLevel,
  createBoss,
  createEnemy,
  createTower,
  enemyColor,
  hpMultiplier,
  refreshEnemyTrack,
  waveSpeedRamp,
  type Enemy,
  type ProjectileKind,
  type Tower,
} from './entities';
import {
  CORE_CELL,
  PATH,
  PATH_LENGTH,
  SPAWN_CELL,
  activeMap,
  cellCenter,
  inBounds,
  isBlockedTile,
  isEndpoint,
  isPathCell,
  pointAtPath,
  type Cell,
  type Point,
} from './map';
import { applyEndlessModifier, pickEndlessModifier } from './endless';
import { findPath, floodReachable, tileIndex } from './pathfinding';
import { losBlocked, losFirstHitT } from './los';
import type { GameState } from './state';

/**
 * Minimal sound sink — satisfied by `engine/audio.ts`'s `Audio`.
 *
 * Sprint 35 (audio-int-s35, docs/AUDIO_DESIGN.md §2.2 / §2.3 / §2.4): three
 * optional state-coupled fire cues plug onto the same sink without forcing
 * every consumer to grow them. The headless puzzle-mode evaluator's
 * `SILENT_SFX` (`src/game/puzzle.ts`) still satisfies `Sfx` with just `play`,
 * so the new cues degrade silently when absent (the live game's `Audio` class
 * always provides them — `docs/NEXT_BATCH_PLAN.md` §1.2).
 */
export interface Sfx {
  play(name: SoundName): void;
  /**
   * Arc Coil fire cue pitched to the live grid surplus
   * (`docs/AUDIO_DESIGN.md` §2.4). `surplus` is `state.power.surplus`;
   * `freeband` is `CONFIG.arcSurplusFreeband` — the same constant the damage
   * coupling uses, so the audible band matches the mechanical band.
   */
  playArc?(surplus: number, freeband: number): void;
  /**
   * Sniper Rail fire cue escalating with `piercedCount` enemies hit this
   * shot (`docs/AUDIO_DESIGN.md` §2.2). Clamped onto the 1..5+ tier table by
   * the audio module — the call-site forwards the raw count.
   */
  playSniper?(piercedCount: number): void;
  /**
   * Mortar dead-zone reject click — fires when the Mortar's cooldown is
   * ready, an enemy lies inside `minRange`, and no eligible target lies in
   * the firing annulus (`docs/AUDIO_DESIGN.md` §2.3 cue 2). Throttled
   * per-`towerId` inside the audio module; the return value reports the
   * throttle gate (true = gate opened) for visual-proxy hooks.
   */
  playMortarDeadzone?(towerId: number, tick: number): boolean;
  /**
   * Cryo brittle paint cue — fires when an enemy gains the brittle mark from
   * a Cryo pulse (Sprint 51 polish, `docs/AUDIO_DESIGN.md` §2.5 / additional
   * cues). Throttled per-`enemyId` inside the audio module; the return value
   * reports the throttle gate (true = gate opened) for visual-proxy hooks.
   */
  playCryoBrittle?(enemyId: number, tick: number): boolean;
}

/** Post a transient message to the HUD. */
function notify(state: GameState, text: string): void {
  state.notice = { text, ttl: CONFIG.noticeTicks };
}

/** The active difficulty's multiplier set. */
function diff(state: GameState): DifficultyDef {
  return CONFIG.difficulties[state.difficulty];
}

/**
 * The Overload tier locked in for the wave in progress (`state.waveOverload`).
 * Falls back to tier 0 (balance-neutral) if the index is somehow out of range.
 */
function overloadTier(state: GameState): OverloadTier {
  return CONFIG.overloadTiers[state.waveOverload] ?? CONFIG.overloadTiers[0]!;
}

/**
 * Per-kill bounty-decay factor `D(w)` — see docs/GAME_DESIGN.md §9. Pinned at
 * 1.0 through `bountyDecayStart`, then decays linearly to `bountyDecayFloor`.
 */
function bountyDecay(wave: number): number {
  const decayed = 1 - CONFIG.bountyDecayRate * Math.max(0, wave - CONFIG.bountyDecayStart);
  return Math.max(CONFIG.bountyDecayFloor, decayed);
}

/** Locate an active-ability static definition by id. */
function abilityDef(id: AbilityId): AbilityDef {
  const def = CONFIG.abilities.find((a) => a.id === id);
  if (!def) throw new Error(`unknown ability: ${id}`);
  return def;
}

/** Locate the tower occupying a tile, if any. */
export function towerAt(state: GameState, col: number, row: number): Tower | undefined {
  return state.towers.find((t) => t.col === col && t.row === row);
}

// --- power-state memo cache (P2, Sprint 38 perf-wins) ------------------------

/**
 * Single-slot memo cache for {@link recomputePower}. The grid is a pure
 * function of (tower roster's `supply` / `powerDraw` / `overclock` flags) ×
 * (`drainTicksLeft > 0`) × CONFIG constants — none of which change between
 * ticks during the build phase or during long stretches of a wave with no
 * place / sell / upgrade / Overclock toggle. We hash those inputs into a
 * single integer fingerprint and skip the O(towers) walk + result-object
 * allocation when the fingerprint matches the last call.
 *
 * The cached `PowerState` is re-bound directly into `state.power` on a hit,
 * so any consumer reading `state.power.efficiency` / `state.power.surplus` is
 * byte-identical to a fresh compute. The cache is module-local; it survives
 * across `reset()` (the fingerprint is content-derived, so a fresh state with
 * the same inputs would have produced the same `PowerState` anyway), and it
 * is never re-entered (`recomputePower` is synchronous).
 */
interface PowerMemo {
  fingerprint: number;
  power: GameState['power'];
}
let powerMemo: PowerMemo | null = null;
/**
 * Hit/miss counters exposed for the perf regression test. Pure observation —
 * never read by the sim. The keys live on a single mutable object so a test
 * snapshot can read `{ hits, misses }` without re-importing the module.
 */
export const __powerCacheStats: { hits: number; misses: number; resets: number } = {
  hits: 0,
  misses: 0,
  resets: 0,
};

/**
 * Reset the power-state memo. Exposed only as a test hook — production code
 * never needs to clear it, because the content-derived fingerprint is robust
 * to a `reset()` (an empty-tower fresh state would hash the same as a prior
 * empty-tower state and correctly reuse the cached "no towers" PowerState).
 */
export function __resetPowerCache(): void {
  powerMemo = null;
  __powerCacheStats.hits = 0;
  __powerCacheStats.misses = 0;
  __powerCacheStats.resets += 1;
}

/**
 * Order-independent content fingerprint of every input `recomputePower`
 * actually reads. Per-tower contributions are summed (not stream-mixed) so
 * the hash is stable across roster re-orderings caused by `splice` in
 * `sellTower`. Integer arithmetic via `| 0` keeps the result in the int32
 * domain so a JS engine can keep the variable in a SMI slot.
 */
function powerFingerprint(state: GameState): number {
  let h = state.drainTicksLeft > 0 ? 1 : 0;
  h = (h * 1315423911 + state.towers.length) | 0;
  // CONFIG inputs that recomputePower actually reads — folded in so a
  // future CONFIG hot-reload (tests do mutate config under
  // `g.config.onboardingEnabled = false`) cannot serve a stale value.
  h = (h + Math.round(CONFIG.reactorBasePower * 31)) | 0;
  h = (h + Math.round(CONFIG.overclockDrawMult * 1000)) | 0;
  h = (h + Math.round(CONFIG.bossDrainMult * 1000)) | 0;
  for (const t of state.towers) {
    // Order-independent: per-tower contributions are added, so an array
    // splice (sellTower) does not change the hash for an unchanged roster.
    const ocBit = t.overclock ? 1 : 0;
    h = (h + t.supply * 7919 + t.powerDraw * 6271 + ocBit * 5471) | 0;
  }
  return h;
}

/**
 * Compute and store the grid's PowerState. Split from the cached public entry
 * so the hot path stays cheap (a fingerprint compare + an assignment) and the
 * miss path keeps the original tower walk byte-for-byte intact.
 */
function computePower(state: GameState): GameState['power'] {
  let supply = CONFIG.reactorBasePower;
  let demand = 0;
  let overclockCount = 0;
  // Both `supply` and `powerDraw` are resolved onto each tower (0 on the
  // irrelevant side), so the grid is a straight sum over the roster.
  for (const tower of state.towers) {
    supply += tower.supply;
    // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.2): an Overclocked tower's
    // effective draw is scaled by `overclockDrawMult` and rounded — the
    // extra watts feed straight into `demand`, so an Overclock spike pulls
    // the grid toward brownout and shrinks `surplus` (which the Arc Coil
    // reads via `arcDamageMult`). Default-off → no behaviour change.
    const draw = tower.overclock
      ? Math.round(tower.powerDraw * CONFIG.overclockDrawMult)
      : tower.powerDraw;
    demand += draw;
    if (tower.overclock) overclockCount += 1;
  }
  let efficiency = clamp(supply / Math.max(demand, 1), 0, 1);
  // The Kernel boss's Power Drain forces a brownout: while it is active the
  // grid's base efficiency is multiplied by `bossDrainMult` (docs §17.3). This
  // is applied *before* Overcharge's 1.6 floor (in `applyCooldown`), so firing
  // Overcharge fully cancels a Power Drain for its window.
  if (state.drainTicksLeft > 0) efficiency *= CONFIG.bossDrainMult;
  // `surplus` is the raw structural headroom — the Arc Coil couples to it
  // (§5). It is deliberately NOT touched by the Power Drain: the drain
  // throttles fire *rate* via `efficiency`, it does not change supply/demand.
  return { supply, demand, efficiency, surplus: supply - demand, overclockCount };
}

/**
 * Recompute the power grid from the current tower roster.
 *
 * Sprint 38 perf-wins (docs/PERFORMANCE_AUDIT.md §5.1): this is called once
 * per `simTick` and on every place / sell / upgrade / Overclock toggle. The
 * input set is small and the function is pure, so we memoise the last result
 * by an order-independent content fingerprint of the roster + the drain bit
 * + the CONFIG constants the compute reads. On a hit (the steady state during
 * the build phase, plus long mid-wave stretches with no roster change) the
 * cached `PowerState` is rebound directly into `state.power` — no tower walk,
 * no per-call result-object allocation. The grid is **byte-equivalent** to
 * the un-cached version: the cached value WAS the computed value on a prior
 * matching fingerprint.
 */
export function recomputePower(state: GameState): void {
  const fp = powerFingerprint(state);
  if (powerMemo !== null && powerMemo.fingerprint === fp) {
    state.power = powerMemo.power;
    __powerCacheStats.hits += 1;
    return;
  }
  const power = computePower(state);
  state.power = power;
  powerMemo = { fingerprint: fp, power };
  __powerCacheStats.misses += 1;
}

/**
 * Sprint 49 perf-wins (P3): re-applied / freshly-allocated status records
 * (slow / brittle / corrode / emp) — the apply functions mutate the existing
 * status object in place on re-apply (the steady state during a Cryo paint or
 * scorch-zone burn). Counters published on the perf hook so the regression
 * test can assert the "re-apply path doesn't allocate" invariant without
 * bumping the public `__GAME__` test contract.
 */
export const __statusApplyStats: { reused: number; fresh: number } = {
  reused: 0,
  fresh: 0,
};

/** Sprint 49 perf-wins (P5): array compactions performed in place per tick. */
export const __compactStats: { compactions: number; preserved: number; removed: number } = {
  compactions: 0,
  preserved: 0,
  removed: 0,
};

// Sprint 38 perf-wins (P2) + Sprint 49 perf-wins (P3, P5): expose the perf
// counters on `globalThis` so the regression specs can assert cache-hit /
// re-apply / in-place compaction behaviour without bumping the public
// `__GAME__` test contract. The harness reads
// `window.__OVERVOLT_PERF__` directly; production code never touches it.
(globalThis as {
  __OVERVOLT_PERF__?: {
    powerCacheStats: typeof __powerCacheStats;
    statusApplyStats: typeof __statusApplyStats;
    compactStats: typeof __compactStats;
  };
}).__OVERVOLT_PERF__ = {
  powerCacheStats: __powerCacheStats,
  statusApplyStats: __statusApplyStats,
  compactStats: __compactStats,
};

/**
 * Sprint 49 perf-wins P5 (docs/PERFORMANCE_AUDIT.md §1.3, §2.1 honourable
 * mention): order-preserving in-place compaction. Pre-S49, `simTick` did
 * **four** `state.X = state.X.filter(...)` calls per tick — one for enemies,
 * particles, projectiles, scorch zones — each allocating a fresh backing
 * array even when zero items were removed. Steady-state churn on every
 * single tick.
 *
 * This helper walks the array with a read / write pointer, keeping survivors
 * in their original positions and truncating the length. The resulting array
 * is **byte-equivalent** to `filter` — the same items, the same order — and
 * the engine's caller still observes a `T[]` with the same surviving
 * elements. Re-uses the existing backing array, so a `JSON.stringify(state)`
 * snapshot is identical pre-/post-S49.
 *
 * Determinism note: every existing reader iterates by index over `state.X`
 * (or by `for...of`), so the truncated tail is invisible. No consumer reads
 * `state.X` by reference identity, so reusing the array is safe.
 *
 * The counters tick `__compactStats` so the regression test in
 * `tests/perf-extra.spec.ts` can assert "no realloc on the steady-state
 * tick" without bumping the public `__GAME__` test contract.
 */
function compactInPlace<T>(arr: T[], keep: (item: T) => boolean): void {
  let write = 0;
  const n = arr.length;
  for (let read = 0; read < n; read += 1) {
    const item = arr[read]!;
    if (keep(item)) {
      if (write !== read) arr[write] = item;
      write += 1;
    }
  }
  if (write !== n) {
    arr.length = write;
    __compactStats.removed += n - write;
  }
  __compactStats.preserved += write;
  __compactStats.compactions += 1;
}

/**
 * Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.2) — flip a tower's per-tower
 * Overclock. Pure boolean toggle: only weapon towers may Overclock (the
 * Pylon / Wall have no fire loop, so the call is a quiet no-op there), and
 * the grid is recomputed immediately so the demand spike + `overclockCount`
 * are honest on the very next tick. Returns the new `overclock` flag value;
 * `null` when no tower has `towerId`.
 */
export function toggleOverclock(state: GameState, towerId: number): boolean | null {
  const tower = state.towers.find((t) => t.id === towerId);
  if (!tower) return null;
  if (!CONFIG.towers[tower.type].weapon) return tower.overclock;
  tower.overclock = !tower.overclock;
  recomputePower(state);
  return tower.overclock;
}

/**
 * Sprint 26 (REC 3) — explicit set of a tower's Overclock flag. Mirrors
 * `toggleOverclock` for test scenarios that want an idempotent set (vs. a
 * toggle). Returns `false` when no tower has `towerId` or it is a non-weapon
 * (Pylon / Wall), otherwise `true`.
 */
export function setOverclock(state: GameState, towerId: number, on: boolean): boolean {
  const tower = state.towers.find((t) => t.id === towerId);
  if (!tower) return false;
  if (!CONFIG.towers[tower.type].weapon) return false;
  if (tower.overclock === on) return true;
  tower.overclock = on;
  recomputePower(state);
  return true;
}

/**
 * Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B + docs/NEXT_BATCH_PLAN.md
 * §2.6) — refresh every weapon tower's **Pylon-aura** fire-rate cache. Pure
 * geometric pass over the integer tile centres: for each weapon tower, count
 * Pylons whose centre sits inside `pylonAuraRadius × tileSize` pixels (cap at
 * `pylonAuraStackCap`), and write the resulting multiplier `1 + n ×
 * pylonAuraRateBuff` to `tower.auraRateMult`. `applyCooldown` reads the
 * cache once per fire — so the only sites that *write* it are the four
 * placement-state mutators (`placeTower`, `sellTower`, `upgradeTower`, and
 * this function for explicit refreshes from tests). No RNG, no enemies
 * touched: identical placement → identical cache.
 *
 * Lever choice (per the brief). **Fire-rate only**, not range. Two reasons:
 *  - Fire-rate composes through one line in `applyCooldown` (an extra
 *    divisor) without touching `tower.range`, the targeting filter, the
 *    render range-ring radii, or the Mortar's `minRange` annulus geometry —
 *    so the lever is a one-cache addition, the smallest possible
 *    risk-surface change to a contract-1.17.0 keystone follow-on.
 *  - It is *symmetric* with the existing Sprint-26 Overclock divisor —
 *    Overclock is the in-wave per-tower rate spike, the Pylon aura is the
 *    build-phase placement-bound rate buff. They multiply cleanly through
 *    the same divisor without double-counting any other axis.
 *
 * Pylons / Walls keep `auraRateMult` at `1` (inert) — they have no fire
 * loop, so the cache is moot but the field is still a clean default.
 *
 * Cap stack interaction with Overclock. Per `docs/REC3_DESIGN.md` §4.5 the
 * Overclock divisor (`1.6×` at default) lives on top of the aura divisor
 * (`≤ 1 + 2 × 0.10 = 1.20×` at the cap). They multiply (a saturated tower
 * Overclocked fires at `1.92×`) — *not* additively, so neither lever crowds
 * the other out. Overclock still drives `demand` (its watts), the aura
 * does not — so Overclock + Arc-surplus coupling (Sprint 15) remains the
 * "spend surplus" story the Sprint-26 keystone shipped on.
 *
 * Determinism: pure function of `state.towers[].(type, col, row)` + three
 * config scalars. Idempotent (recomputed values overwrite the same cache
 * slots), so calling it twice in a row is a no-op past the first.
 */
export function applyPylonAuras(state: GameState): void {
  const ts = CONFIG.grid.tileSize;
  const radiusPx = CONFIG.pylonAuraRadius * ts;
  const radiusSq = radiusPx * radiusPx;
  const cap = Math.max(0, CONFIG.pylonAuraStackCap);
  const buff = CONFIG.pylonAuraRateBuff;
  // Pre-resolve the Pylon centres into one small list — typical roster has
  // ≤ 10 Pylons, so the N×M loop below stays trivial. Pylons whose level
  // doesn't matter (the supply axis is a separate Sprint-15 concern); the
  // aura is binary per-Pylon at this lever — present or not.
  const pylons: { x: number; y: number }[] = [];
  for (const tower of state.towers) {
    if (tower.type !== 'pylon') continue;
    pylons.push(cellCenter(tower.col, tower.row));
  }
  for (const tower of state.towers) {
    // Non-weapons (Pylon / Wall) carry an inert cache — defensive default so
    // a future field read can never see `undefined` after a fresh place.
    if (!CONFIG.towers[tower.type].weapon) {
      tower.auraRateMult = 1;
      continue;
    }
    if (pylons.length === 0 || cap === 0 || buff === 0) {
      tower.auraRateMult = 1;
      continue;
    }
    const center = cellCenter(tower.col, tower.row);
    let n = 0;
    for (const p of pylons) {
      if (dist2(center.x, center.y, p.x, p.y) <= radiusSq) {
        n += 1;
        if (n >= cap) break;
      }
    }
    tower.auraRateMult = 1 + n * buff;
  }
}

/**
 * Arc Coil power-surplus coupling (docs/GAME_DESIGN.md §5/§6). The Arc's
 * per-shot damage is multiplied by a pure deterministic function of the live
 * grid surplus (`supply − demand`). A *roughly managed* grid — a mild surplus
 * OR a mild deficit, anywhere inside the symmetric free band ±`arcSurplus
 * Freeband` — leaves the Arc neutral (×1.0). Only a deliberate Pylon
 * over-build past the band ramps it up to `1 + arcSurplusDamageCap`, and only
 * a *real* brownout past the band ramps it down to `arcBrownoutDamageFloor`.
 * Integer-in, no RNG — determinism-safe.
 */
export function arcDamageMult(surplus: number): number {
  const band = CONFIG.arcSurplusFreeband;
  let eff = 0;
  if (surplus > band) eff = surplus - band;
  else if (surplus < -band) eff = surplus + band;
  return clamp(
    1 + eff * CONFIG.arcSurplusDamagePerPoint,
    CONFIG.arcBrownoutDamageFloor,
    1 + CONFIG.arcSurplusDamageCap,
  );
}

/**
 * Bonus Arc chain targets from a rich grid surplus past the free band
 * (docs/GAME_DESIGN.md §5) — capped at `arcSurplusChainMax`, and 0 for any
 * surplus inside the free band or in a brownout.
 */
export function arcChainBonus(surplus: number): number {
  const eff = surplus - CONFIG.arcSurplusFreeband;
  if (eff <= 0) return 0;
  return Math.min(CONFIG.arcSurplusChainMax, Math.floor(eff / CONFIG.arcSurplusChainPer));
}

// --- maze pathfinding (docs/GAME_DESIGN.md §16) -------------------------------

/**
 * The set of tile indices the Maze A* / flood-fill treat as impassable:
 *  - every placed tower (Walls + weapons + the Pylon);
 *  - the active map's permanent blocked terrain (Sprint 21 cramped maps) —
 *    so a non-default map carries its terrain over to Maze Mode too.
 *
 * `state.path` itself is *not* in the set: enemies must walk that path on a
 * fixed-path map, and Maze Mode opens those tiles back up (§16.5).
 */
function blockedTowerTiles(state: GameState): Set<number> {
  const cols = CONFIG.grid.cols;
  const blocked = new Set<number>();
  for (const tower of state.towers) blocked.add(tileIndex(tower.col, tower.row, cols));
  for (const cell of activeMap().blocked) blocked.add(tileIndex(cell.col, cell.row, cols));
  return blocked;
}

/**
 * The tile(s) an alive enemy reserves against placement (§16.5 rule 3): the
 * tile it is leaving and the tile it is entering this inter-tile step. When the
 * enemy sits exactly on a tile (or is at the Core) it is not mid-step, so it
 * reserves only that single tile — which is exactly the tile {@link
 * enemyCommitTile} re-paths it from, keeping the two in lockstep.
 */
function enemyReservedTiles(enemy: Enemy): readonly Cell[] {
  const maxIdx = enemy.path.length - 1;
  let i = Math.floor(enemy.pathDist);
  if (i < 0) i = 0;
  else if (i > maxIdx) i = maxIdx;
  const leaving = enemy.path[i]!;
  const frac = enemy.pathDist - i;
  // Exactly on a tile (frac 0) or at the Core: only the current tile.
  if (frac <= 0 || i >= maxIdx) return [leaving];
  return [leaving, enemy.path[i + 1]!];
}

/**
 * The tile an enemy would re-path from after a field change (§16.6): the tile
 * it is committed to walking into — or its current tile when it sits exactly
 * on one. The §16.4 validation guarantees this tile stays Core-reachable.
 */
function enemyCommitTile(enemy: Enemy): Cell {
  const maxIdx = enemy.path.length - 1;
  let i = Math.floor(enemy.pathDist);
  if (i < 0) i = 0;
  if (i >= maxIdx) return enemy.path[maxIdx]!;
  const frac = enemy.pathDist - i;
  return frac <= 0 ? enemy.path[i]! : enemy.path[i + 1]!;
}

/**
 * The no-wall-off rule (§16.4): would adding a blocker at (col, row) leave the
 * spawn — or any alive ground enemy's committed tile — with no route to the
 * Core? Validated by one reverse BFS flood-fill from the Core tile.
 */
export function wallsOffCore(state: GameState, col: number, row: number): boolean {
  const cols = CONFIG.grid.cols;
  const rows = CONFIG.grid.rows;
  const blocked = blockedTowerTiles(state);
  blocked.add(tileIndex(col, row, cols));
  const reachable = floodReachable(CORE_CELL, blocked, cols, rows);
  if (!reachable.has(tileIndex(SPAWN_CELL.col, SPAWN_CELL.row, cols))) return true;
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const t = enemyCommitTile(enemy);
    if (!reachable.has(tileIndex(t.col, t.row, cols))) return true;
  }
  return false;
}

/**
 * Recompute the global route and every alive enemy's route after the blocked
 * set changes. `state.path` becomes the fresh A* spawn→Core route; each enemy
 * re-paths deterministically from its committed tile, pixel position preserved
 * (§16.6).
 *
 * Outside Maze Mode this **restores the fixed S-path** rather than no-op'ing:
 * a `setMode` toggle (Campaign→Maze→Campaign) rebuilds the maze route into
 * `state.path`, so the toggle back must put the fixed `PATH` back — otherwise
 * the next wave's enemies would run the stale straight maze route.
 */
export function recomputePaths(state: GameState): void {
  if (state.mode !== 'maze') {
    state.path = PATH;
    state.mazePathLength = PATH_LENGTH;
    return;
  }
  const cols = CONFIG.grid.cols;
  const rows = CONFIG.grid.rows;
  const blocked = blockedTowerTiles(state);
  const route = findPath(SPAWN_CELL, CORE_CELL, blocked, cols, rows);
  if (route) {
    state.path = route;
    state.mazePathLength = route.length - 1;
  }
  for (const enemy of state.enemies) {
    if (enemy.alive) rePathEnemy(enemy, blocked, cols, rows);
  }
}

/**
 * Re-path one alive enemy from its committed tile to the Core (§16.6). The
 * enemy finishes its current step into the committed tile, then follows the
 * new A* route — so its pixel position never teleports and it never steps
 * backward. A null route (cannot happen once §16.4 holds) leaves it untouched.
 */
function rePathEnemy(
  enemy: Enemy,
  blocked: ReadonlySet<number>,
  cols: number,
  rows: number,
): void {
  const oldPath = enemy.path;
  const maxIdx = oldPath.length - 1;
  let i = Math.floor(enemy.pathDist);
  if (i < 0) i = 0;
  if (i >= maxIdx) return; // at/past the Core — about to be culled this tick.
  const frac = enemy.pathDist - i;
  if (frac <= 0) {
    // Exactly on a tile: re-path straight from it.
    const route = findPath(oldPath[i]!, CORE_CELL, blocked, cols, rows);
    // A non-null A* route always includes at least its start tile.
    if (route) {
      enemy.path = route;
      enemy.pathDist = 0;
    }
  } else {
    // Mid-step: keep the leaving tile, then follow A* from the entering tile.
    const route = findPath(oldPath[i + 1]!, CORE_CELL, blocked, cols, rows);
    if (route) {
      enemy.path = [oldPath[i]!, ...route];
      enemy.pathDist = frac;
    }
  }
  enemy.prevPathDist = enemy.pathDist;
  refreshEnemyTrack(enemy);
}

/**
 * Test hook (§16.8): the deterministic A* route from tile (col, row) to the
 * Core for the current blocked set, or `null` when the tile is walled off.
 */
export function pathFrom(state: GameState, col: number, row: number): Cell[] | null {
  if (!inBounds(col, row)) return null;
  return findPath(
    { col, row },
    CORE_CELL,
    blockedTowerTiles(state),
    CONFIG.grid.cols,
    CONFIG.grid.rows,
  );
}

// --- tower placement ---------------------------------------------------------

/** Why a placement is rejected, or `null` when it is legal. */
export type PlacementReject =
  | 'phase'
  | 'tile'
  | 'mode'
  | 'occupied'
  | 'onEnemy'
  | 'gold'
  | 'wallsOff'
  | null;

/**
 * Classify a prospective placement of `type` at (col, row) — a pure probe that
 * never mutates state. Drives {@link canPlace}, {@link placeTower} and the
 * renderer's placement-ghost tint (so the §16.4 flood-fill runs once a frame).
 */
export function placementReject(
  state: GameState,
  type: TowerType,
  col: number,
  row: number,
): PlacementReject {
  if (state.phase !== 'build' && state.phase !== 'wave') return 'phase';
  if (!inBounds(col, row)) return 'tile';
  const maze = state.mode === 'maze';
  // The Wall is a Maze-Mode placeable only — on a fixed-path map there is no
  // route to shape, so it has nothing to do (docs/GAME_DESIGN.md §16.9).
  if (type === 'wall' && !maze) return 'mode';
  if (maze) {
    // Open field: only the spawn / Core endpoints are off-limits (§16.5).
    if (isEndpoint(col, row)) return 'tile';
    // The active map's permanent terrain (Sprint 21 cramped maps) is also
    // unbuildable even in Maze Mode — terrain walls are not towers.
    if (isBlockedTile(col, row)) return 'tile';
  } else if (isPathCell(col, row) || isBlockedTile(col, row)) {
    // Fixed-path map: the enemy path AND the active map's permanent blocked
    // terrain (Sprint 21) are not buildable. Both are surfaced as the same
    // `'tile'` reject — the player only needs to know "can't build there".
    return 'tile';
  }
  if (towerAt(state, col, row)) return 'occupied';
  if (maze) {
    for (const enemy of state.enemies) {
      if (!enemy.alive) continue;
      for (const t of enemyReservedTiles(enemy)) {
        if (t.col === col && t.row === row) return 'onEnemy';
      }
    }
  }
  if (state.gold < CONFIG.towers[type].cost) return 'gold';
  if (maze && wallsOffCore(state, col, row)) return 'wallsOff';
  return null;
}

/**
 * Pure validation probe (§16.8): would placing `type` at (col, row) succeed —
 * affordability, occupancy, and in Maze Mode the no-wall-off path rule? Never
 * mutates state.
 */
export function canPlace(state: GameState, type: TowerType, col: number, row: number): boolean {
  return placementReject(state, type, col, row) === null;
}

/**
 * Attempt to place a tower of `type` on tile (col, row). Returns true on
 * success. Valid during the build and wave phases. In Maze Mode a placement
 * that would seal the Core off (§16.4) is rejected with no gold spent and the
 * route untouched; a successful place recomputes every route (§16.3).
 */
export function placeTower(
  state: GameState,
  type: TowerType,
  col: number,
  row: number,
  sfx: Sfx,
): boolean {
  const reject = placementReject(state, type, col, row);
  if (reject !== null) {
    switch (reject) {
      case 'tile':
        notify(state, 'Cannot build there');
        break;
      case 'mode':
        notify(state, 'Walls are Maze Mode only');
        break;
      case 'occupied':
        notify(state, 'Tile occupied');
        break;
      case 'onEnemy':
        notify(state, 'An enemy is in the way');
        break;
      case 'wallsOff':
        notify(state, 'Would seal off the Core');
        break;
      case 'gold':
        notify(state, 'Not enough gold');
        sfx.play('error');
        break;
      case 'phase':
        break;
    }
    return false;
  }
  state.gold -= CONFIG.towers[type].cost;
  state.towers.push(createTower(state.nextId++, type, col, row));
  recomputePower(state);
  // Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B): refresh every weapon
  // tower's Pylon-aura fire-rate cache. A new Pylon may buff its neighbours;
  // a new weapon tower may inherit the buff of pre-existing Pylons; either
  // way the cache is now stale.
  applyPylonAuras(state);
  // Maze Mode: the new blocker reshapes the global route + every enemy route.
  recomputePaths(state);
  sfx.play('place');
  return true;
}

/** Sell the tower on tile (col, row), refunding part of its total spend. */
export function sellTower(state: GameState, col: number, row: number, sfx: Sfx): boolean {
  const idx = state.towers.findIndex((t) => t.col === col && t.row === row);
  if (idx < 0) return false;
  const tower = state.towers[idx]!;
  const refund = Math.floor(tower.totalSpent * CONFIG.sellRefundRate);
  state.gold += refund;
  if (state.selectedTowerId === tower.id) state.selectedTowerId = null;
  state.towers.splice(idx, 1);
  recomputePower(state);
  // Sprint 39 (REC 3.b): selling a Pylon strips its aura contribution from
  // every neighbour; selling a weapon tower simply removes a target of the
  // aura. Either way the cache is now stale — refresh once.
  applyPylonAuras(state);
  // Removing a blocker can only open routes — no validation needed (§16.4).
  recomputePaths(state);
  sfx.play('sell');
  notify(state, `Sold +${refund}g`);
  return true;
}

/**
 * Upgrade the tower on tile (col, row) to its next level. Valid in the build
 * and wave phases; capped at `CONFIG.maxTowerLevel`. The upgrade cost is added
 * to `totalSpent` (so the sell refund reflects it) and the power grid
 * recomputes immediately. Returns true on success.
 */
export function upgradeTower(state: GameState, col: number, row: number, sfx: Sfx): boolean {
  if (state.phase !== 'build' && state.phase !== 'wave') return false;
  const tower = towerAt(state, col, row);
  if (!tower) return false;
  // The Wall is an inert structure — it has nothing to upgrade (§16.9).
  if (CONFIG.towers[tower.type].upgradable === false) {
    notify(state, `${CONFIG.towers[tower.type].label} cannot be upgraded`);
    return false;
  }
  if (tower.level >= CONFIG.maxTowerLevel) {
    notify(state, 'Already at max level');
    return false;
  }
  const cost = CONFIG.towers[tower.type].upgradeCosts[tower.level - 1];
  if (cost === undefined) return false;
  if (state.gold < cost) {
    notify(state, 'Not enough gold');
    sfx.play('error');
    return false;
  }
  state.gold -= cost;
  tower.level += 1;
  tower.totalSpent += cost;
  applyTowerLevel(tower);
  recomputePower(state);
  // Sprint 39 (REC 3.b): the Pylon roster is unchanged so the aura inputs
  // are stable — but a freshly upgraded Pylon costs the player gold, and
  // refreshing the cache here keeps `Tower.auraRateMult` honest under any
  // future config change to `pylonAuraRateBuff` mid-run (test API). Cheap.
  applyPylonAuras(state);
  sfx.play('upgrade');
  notify(state, `${CONFIG.towers[tower.type].label} → L${tower.level}`);
  return true;
}

/** Every targeting mode, in the order the `T` key / panel button cycles them. */
export const TARGET_MODES: readonly TargetMode[] = [
  'first',
  'last',
  'strongest',
  'weakest',
  'nearest',
];

/**
 * Set the targeting mode of the weapon tower on tile (col, row). Valid in any
 * phase. Returns false when no tower is there or it is the (non-weapon) Pylon.
 */
export function setTowerTargetMode(
  state: GameState,
  col: number,
  row: number,
  mode: TargetMode,
): boolean {
  const tower = towerAt(state, col, row);
  if (!tower || !CONFIG.towers[tower.type].weapon) return false;
  tower.targetMode = mode;
  return true;
}

/**
 * Advance the weapon tower on tile (col, row) to the next targeting mode in
 * cycle order — the `T` key / panel Targeting button. Returns true on success.
 */
export function cycleTowerTargetMode(state: GameState, col: number, row: number): boolean {
  const tower = towerAt(state, col, row);
  if (!tower || !CONFIG.towers[tower.type].weapon) return false;
  const next =
    TARGET_MODES[(TARGET_MODES.indexOf(tower.targetMode) + 1) % TARGET_MODES.length]!;
  tower.targetMode = next;
  notify(state, `${CONFIG.towers[tower.type].label} ▸ ${next}`);
  return true;
}

/** Begin the next wave. Only valid from the build phase. Returns true on start. */
export function startWave(state: GameState, sfx: Sfx): boolean {
  if (state.phase !== 'build') return false;
  // Campaign stops at the last table wave; Endless runs forever.
  if (state.mode === 'campaign' && state.wave >= CONFIG.waves.length) return false;
  state.wave += 1;
  state.phase = 'wave';
  state.paused = false;
  // Lock the player's sticky Overload tier in for the wave now starting; a
  // fresh wave starts with no leftover Overcharge / aftershock / scorch zones
  // / Power Drain (§15 / §17). Ability cooldowns survive across waves, but
  // every in-flight transient effect is cleared.
  state.waveOverload = state.overload;
  // Track the deepest Overload pushed this run — recorded into the Scoreboard.
  if (state.waveOverload > state.overloadMax) state.overloadMax = state.waveOverload;
  state.overchargeTicksLeft = 0;
  state.aftershockTicksLeft = 0;
  state.scorchZones = [];
  state.drainTicksLeft = 0;
  state.drainCharge = 0;
  state.drainCooldown = 0;
  state.director = { groupIndex: 0, spawnedInGroup: 0, cooldown: 0, done: false };
  // Campaign group counts / the Endless budget scale by the difficulty count
  // multiplier and the Overload tier's count multiplier together.
  const countMult = diff(state).enemyCountMult * overloadTier(state).countMult;
  state.activeWave = genWave(state.seed, state.wave, countMult);
  // Endless and Maze both keep a deepest-wave record (Maze for its Endless
  // continuation — §9); Campaign has no such record.
  if (state.mode !== 'campaign' && state.wave > state.bestWave) state.bestWave = state.wave;
  sfx.play('wave-start');
  notify(state, `Wave ${state.wave} incoming`);
  return true;
}

/**
 * Set the Overload tier selected for the *next* wave — see docs/GAME_DESIGN.md
 * §8. Build phase only; `tier` must be an integer in `[0, overloadTiers)`.
 * Returns true on success.
 */
export function setOverload(state: GameState, tier: number): boolean {
  if (state.phase !== 'build') return false;
  if (!Number.isInteger(tier) || tier < 0 || tier >= CONFIG.overloadTiers.length) return false;
  state.overload = tier;
  return true;
}

/** End the run, compute the final score, and refresh the high score. */
function endRun(state: GameState, won: boolean, sfx: Sfx): void {
  // Run exactly once: a tick that leaks several enemies, or a stray win check
  // after a loss, must not re-resolve or re-cue the end of the run.
  if (state.phase === 'won' || state.phase === 'lost') return;
  state.phase = won ? 'won' : 'lost';
  let final = state.score + state.lives * CONFIG.livesScoreBonus;
  if (won && state.lives >= state.startLives) {
    final = Math.round(final * CONFIG.winFlourishMultiplier);
  }
  state.finalScore = final;
  if (final > state.highScore) state.highScore = final;
  sfx.play(won ? 'win' : 'lose');
}

// --- status effects ----------------------------------------------------------

/** An enemy's current speed in tiles/s, after any active slow and difficulty. */
function effectiveSpeed(enemy: Enemy, speedMult: number): number {
  // An active EMP hard-stops the enemy; it overrides any slow.
  if (enemy.statuses.emp) return 0;
  const slowed = enemy.statuses.slow ? enemy.speed * enemy.statuses.slow.factor : enemy.speed;
  return slowed * speedMult;
}

/** Clear any status whose timer has elapsed. */
function expireStatuses(enemy: Enemy, tick: number): void {
  if (enemy.statuses.slow && tick >= enemy.statuses.slow.expiresTick) {
    enemy.statuses.slow = null;
  }
  if (enemy.statuses.corrode && tick >= enemy.statuses.corrode.expiresTick) {
    enemy.statuses.corrode = null;
    recomputeArmor(enemy);
  }
  if (enemy.statuses.brittle && tick >= enemy.statuses.brittle.expiresTick) {
    enemy.statuses.brittle = null;
  }
  if (enemy.statuses.emp && tick >= enemy.statuses.emp.expiresTick) {
    enemy.statuses.emp = null;
  }
}

/**
 * Apply (or refresh) an EMP stun on `enemy`. EMP is binary — it never stacks;
 * a reapply just refreshes the timer to `tick + duration`. The Arc Coil L3
 * capstone uses the default `empDurationTicks`; the EMP Burst ability passes
 * its own (longer) duration.
 *
 * Sprint 49 perf-wins P3 (docs/PERFORMANCE_AUDIT.md §1.2, §2.1 — hot per-tick
 * per-enemy status loop): on re-apply, the existing status object is mutated
 * in place rather than reallocated. Byte-equivalent — the status field
 * shape is identical and every reader observes the same `expiresTick`. The
 * counters tick `__statusApplyStats` so the regression test can lock the
 * "re-apply path reuses the object" invariant.
 */
function applyEmp(enemy: Enemy, tick: number, duration: number = CONFIG.empDurationTicks): void {
  const expiresTick = tick + duration;
  const current = enemy.statuses.emp;
  if (current) {
    current.expiresTick = expiresTick;
    __statusApplyStats.reused += 1;
  } else {
    enemy.statuses.emp = { expiresTick };
    __statusApplyStats.fresh += 1;
  }
}

/**
 * Apply (or refresh) a slow on `enemy`. Slows never stack: the strongest
 * factor wins (`min`), and the timer always refreshes to `tick + duration`.
 * The Kernel boss resists slows — an incoming `factor` is softened toward 1
 * by `bossSlowResist` (docs §17.1), so Cryo helps but cannot freeze the fight.
 *
 * Sprint 31 (docs/ENEMY_DESIGN.md §3.2 (a)) — the **Streak** carries the same
 * softening branch on its own `streakSlowResist` tunable: an L3 Cryo's 0.30
 * slow factor settles at 0.72 against a Streak (the design anchor). The
 * Cryo painter's **brittle** leg still applies fully — `applyBrittle` has no
 * Streak branch, by design (the brittle uplift is the cluster's residual
 * purchase on the Streak, keeping the painter's chokepoint role relevant).
 */
function applySlow(enemy: Enemy, factor: number, duration: number, tick: number): void {
  let incoming = factor;
  if (enemy.type === 'kernel') {
    incoming = 1 - (1 - factor) * (1 - CONFIG.bossSlowResist);
  } else if (enemy.type === 'streak') {
    incoming = 1 - (1 - factor) * (1 - CONFIG.streakSlowResist);
  }
  const current = enemy.statuses.slow;
  const strongest = current ? Math.min(current.factor, incoming) : incoming;
  const expiresTick = tick + duration;
  // Sprint 49 perf-wins P3 — mutate the existing status record in place on
  // re-apply (Cryo paints every tick during a fight). Byte-equivalent: the
  // resulting `{factor, expiresTick}` is the same whether the slot is reused
  // or freshly allocated; no consumer reads the object's identity.
  if (current) {
    current.factor = strongest;
    current.expiresTick = expiresTick;
    __statusApplyStats.reused += 1;
  } else {
    enemy.statuses.slow = { factor: strongest, expiresTick };
    __statusApplyStats.fresh += 1;
  }
}

/**
 * Apply (or refresh) the Cryo Emitter's **brittle** damage-vulnerability mark
 * on `enemy` — docs/GAME_DESIGN.md §5. Strongest amp wins (`max`) and the
 * timer always refreshes — like slow, never compounding. Unlike the slow leg,
 * the Kernel boss does **not** resist this; the painter has a relevant boss
 * role even when its slow leg is softened (§17.1).
 */
function applyBrittle(enemy: Enemy, amp: number, duration: number, tick: number): void {
  const current = enemy.statuses.brittle;
  const strongest = current ? Math.max(current.amp, amp) : amp;
  const expiresTick = tick + duration;
  // Sprint 49 perf-wins P3 — mutate-in-place on re-apply. Every scorch-zone
  // tick refreshes brittle on every enemy in radius; this saves the per-
  // refresh literal allocation. Byte-equivalent (field-for-field).
  if (current) {
    current.amp = strongest;
    current.expiresTick = expiresTick;
    __statusApplyStats.reused += 1;
  } else {
    enemy.statuses.brittle = { amp: strongest, expiresTick };
    __statusApplyStats.fresh += 1;
  }
}

/** Recompute an enemy's effective armor from its base armor and Corrosion. */
function recomputeArmor(enemy: Enemy): void {
  const stacks = enemy.statuses.corrode ? enemy.statuses.corrode.stacks : 0;
  enemy.armor = Math.max(0, enemy.baseArmor - stacks * CONFIG.corrodeArmorPerStack);
}

/**
 * Apply (or refresh) Corrosion on `enemy`. Corrosion *stacks*: each
 * application adds one stack (capped at `corrodeMaxStacks`) and refreshes the
 * timer. Inert on unarmored enemies — `recomputeArmor` just floors at 0.
 */
function applyCorrode(enemy: Enemy, tick: number): void {
  const current = enemy.statuses.corrode;
  const stacks = Math.min((current ? current.stacks : 0) + 1, CONFIG.corrodeMaxStacks);
  const expiresTick = tick + CONFIG.corrodeDurationTicks;
  // Sprint 49 perf-wins P3 — mutate-in-place on stack-up / refresh. Every
  // Mortar blast applies corrode to each enemy in the blast radius; saves
  // the per-blast literal allocation. Byte-equivalent.
  if (current) {
    current.stacks = stacks;
    current.expiresTick = expiresTick;
    __statusApplyStats.reused += 1;
  } else {
    enemy.statuses.corrode = { stacks, expiresTick };
    __statusApplyStats.fresh += 1;
  }
  recomputeArmor(enemy);
}

// --- wave composition --------------------------------------------------------

/**
 * Fixed enemy-type pick order (the value is the rank). It is a
 * `Record<EnemyType, …>`, so a newly-added EnemyType is a compile error here
 * until it is given a rank — keeping weighted Endless picks reproducible.
 */
const ENDLESS_PICK_ORDER: Record<EnemyType, number> = {
  bit: 0,
  packet: 1,
  fork: 2,
  daemon: 3,
  cipher: 4,
  patch: 5,
  buffer: 6,
  streak: 7,
};
const ENDLESS_ORDER: readonly EnemyType[] = (
  Object.keys(ENDLESS_PICK_ORDER) as EnemyType[]
).sort((a, b) => ENDLESS_PICK_ORDER[a] - ENDLESS_PICK_ORDER[b]);

/** Pick an enemy type from `weights`, iterating `ENDLESS_ORDER` for determinism. */
function weightedPick(rng: Rng, weights: Record<EnemyType, number>): EnemyType {
  let total = 0;
  for (const t of ENDLESS_ORDER) total += weights[t];
  let r = rng.next() * total;
  for (const t of ENDLESS_ORDER) {
    r -= weights[t];
    if (r < 0) return t;
  }
  return ENDLESS_ORDER[ENDLESS_ORDER.length - 1]!;
}

/**
 * Procedurally generate an Endless wave (`w` beyond the campaign table). A
 * pure function of `(seed, w, countMult)` via a per-wave PRNG — the
 * difficulty `enemyCountMult` scales the threat budget — so it is fully
 * reproducible and independent of how the run has played out.
 */
function generateWave(seed: number, w: number, countMult: number): WaveDef {
  const rng = new Rng((seed ^ Math.imul(w, 0x9e3779b9)) >>> 0);
  // Re-anchored to the campaign length (`waves.length`, kept equal to
  // `campaignLength`) — the procedural curve has the exact same shape it had
  // at 15 waves, it simply begins at wave 11 now (§14).
  const beyond = w - CONFIG.waves.length;
  const surge = w % CONFIG.endlessSurgeEvery === 0;
  let budget = CONFIG.endlessBudgetBase + CONFIG.endlessBudgetPerWave * beyond;
  const weights: Record<EnemyType, number> = { ...CONFIG.endlessWeights };
  if (surge) {
    budget *= CONFIG.endlessSurgeMult;
    weights.daemon *= 2;
    weights.cipher *= 2;
    weights.buffer *= 2;
  }
  budget = Math.round(budget * countMult);
  let interval = clamp(Math.round(8 - beyond * 0.2), 3, 8);

  // Sprint 47: per-wave Endless modifier — pure function of `(seed, w)`,
  // applied AFTER the surge boost (modifier waves are guaranteed non-surge,
  // see `isEndlessModifierWave`). Composition-only — no new combat behaviour.
  // Campaign waves (1–10), surge waves, and boss-milestone waves return null
  // and the no-modifier path is byte-identical to pre-S47.
  const modifier = pickEndlessModifier(seed, w);
  if (modifier !== null) {
    const adjusted = applyEndlessModifier(weights, interval, modifier);
    interval = adjusted.interval;
  }

  const picks: EnemyType[] = [];
  // Sane config has min threat cost ≥ 1; the guard bounds a zero-cost misconfig.
  for (let guard = 0; budget > 0 && guard < 4000; guard += 1) {
    const type = weightedPick(rng, weights);
    picks.push(type);
    budget -= CONFIG.endlessThreatCost[type];
  }
  // Merge consecutive identical picks into groups.
  const groups: WaveGroup[] = [];
  for (const type of picks) {
    const last = groups[groups.length - 1];
    if (last && last.type === type) last.count += 1;
    else groups.push({ type, count: 1, interval });
  }
  // Boss milestone: append a single Kernel as the wave's final group (§17.4).
  // Every milestone is also a surge wave (all divisible by 5).
  if (isEndlessBossWave(w)) groups.push({ type: 'kernel', count: 1, interval: 1 });
  const def: WaveDef = { groups };
  if (modifier !== null) def.modifier = modifier.id;
  return def;
}

/**
 * The composition of wave `w` — the Campaign table for `w` within the table,
 * otherwise a procedurally generated Endless wave. Group counts are scaled by
 * the difficulty `countMult` (campaign) / the budget (Endless). A fresh copy.
 */
export function genWave(seed: number, w: number, countMult: number): WaveDef {
  if (w >= 1 && w <= CONFIG.waves.length) {
    const base = CONFIG.waves[w - 1]!;
    return {
      groups: base.groups.map((g) => ({
        type: g.type,
        // The Kernel boss group is always exactly one boss — Overload (and any
        // difficulty count multiplier) never multiplies the boss count (§17.4).
        count: g.type === 'kernel' ? g.count : Math.max(1, Math.round(g.count * countMult)),
        interval: g.interval,
      })),
    };
  }
  return generateWave(seed, w, countMult);
}

/**
 * True when Endless wave `w` is a boss-milestone wave — `w ≥ endlessBossFirst`
 * (20) on the `endlessBossEvery` (10) cadence: waves 20, 30, 40, … (§17.4).
 */
export function isEndlessBossWave(w: number): boolean {
  return (
    w >= CONFIG.endlessBossFirst &&
    (w - CONFIG.endlessBossFirst) % CONFIG.endlessBossEvery === 0
  );
}

// --- the Kernel boss (docs/GAME_DESIGN.md §17) -------------------------------

/**
 * The route + start distance a spawn co-located with `origin` should use: in
 * Maze Mode a fresh A* route from the origin's current tile, otherwise the
 * origin's own route + `pathDist`. Shared by Fork-child splitting (§5) and the
 * Kernel's phase-spawned adds (§17.2).
 */
function coLocatedSpawn(state: GameState, origin: Enemy): { path: readonly Cell[]; dist: number } {
  if (state.mode === 'maze') {
    const route = findPath(
      origin.tile,
      CORE_CELL,
      blockedTowerTiles(state),
      CONFIG.grid.cols,
      CONFIG.grid.rows,
    );
    if (route && route.length > 0) return { path: route, dist: 0 };
  }
  return { path: origin.path, dist: origin.pathDist };
}

/**
 * Spawn the Kernel boss into the wave in progress and arm its Power-Drain
 * cycle. The boss walks the current global route (the fixed S-path or the live
 * A* maze route); in Maze Mode it re-paths like any ground unit (§16.6 / §17).
 */
function spawnKernel(state: GameState, hpMult: number, path: readonly Cell[]): void {
  state.enemies.push(createBoss(state.nextId++, state.wave, hpMult, path));
  // Arm the first Power-Drain cycle — a charge begins after one phase-1 interval.
  state.drainCharge = 0;
  state.drainCooldown = CONFIG.bossDrainInterval[0];
}

/**
 * Test hook (§17.5): inject one Kernel at the spawn tile into the wave in
 * progress so QA can exercise the boss fight without grinding to wave 15.
 * Wave phase only; returns true on success.
 */
export function spawnBoss(state: GameState): boolean {
  if (state.phase !== 'wave') return false;
  const hpMult = diff(state).enemyHpMult * overloadTier(state).hpMult;
  spawnKernel(state, hpMult, state.path);
  return true;
}

/**
 * Spawn the Kernel's phase-entry adds at the boss's current tile (§17.2). They
 * take the wave's effective multipliers and A*-path like Fork children — in
 * Maze Mode from the boss tile, on a fixed path co-located with the boss — and
 * count toward wave-clear via the existing enemy roster.
 *
 * Sprint 42 Kernel rework (`docs/KERNEL_REWORK_DESIGN.md` §3.2 Change 5): pod
 * spawns `bossAddSpawnLead` tiles *ahead* of the boss along the shared route,
 * clamped to the route's last index. This breaks the one-tick boss/pod co-line
 * (the silent Sniper-lance trivialise) and steps the pod out of any Mortar
 * inner-annulus the boss is sitting on. In Maze Mode the lead applies to the
 * fresh A*-re-path that already starts at the boss's tile (`origin.dist === 0`)
 * — the pod walks the same route, just one tile down it.
 */
function spawnBossAdds(state: GameState, boss: Enemy): void {
  const spec = boss.phase === 2 ? CONFIG.bossPhase2Adds : CONFIG.bossPhase3Adds;
  const origin = coLocatedSpawn(state, boss);
  const hpMult = diff(state).enemyHpMult * overloadTier(state).hpMult;
  // Lead the pod one tile down the shared route. `path.length − 1` is the
  // Core tile (the last index any enemy can occupy); clamp into the route.
  const maxIdx = Math.max(0, origin.path.length - 1);
  const spawnDist = Math.min(maxIdx, Math.max(0, origin.dist + CONFIG.bossAddSpawnLead));
  // Iterate a fixed type order so the add ids are assigned deterministically.
  for (const type of ENDLESS_ORDER) {
    const count = spec[type] ?? 0;
    for (let i = 0; i < count; i += 1) {
      const child = createEnemy(state.nextId++, type, state.wave, hpMult, origin.path, boss.id);
      child.pathDist = spawnDist;
      child.prevPathDist = spawnDist;
      refreshEnemyTrack(child);
      state.enemies.push(child);
    }
  }
}

/**
 * Advance one Kernel through any HP-gated phase transitions it has crossed
 * this tick (§17.2). Each crossing increments `phase`, raises the boss's speed
 * to that phase's value and spawns its adds exactly once — a single huge hit
 * that crosses both thresholds fires both. Bounded at 3 phases.
 */
function advanceBossPhase(state: GameState, boss: Enemy): void {
  const frac = boss.hp / boss.maxHp;
  while (
    (boss.phase === 1 && frac < CONFIG.bossPhase2HpFrac) ||
    (boss.phase === 2 && frac < CONFIG.bossPhase3HpFrac)
  ) {
    boss.phase += 1;
    boss.speed = CONFIG.bossPhaseSpeed[boss.phase - 1] ?? boss.speed;
    spawnBossAdds(state, boss);
  }
}

/**
 * One tick of Kernel-boss logic — phase transitions and the telegraphed Power
 * Drain attack (§17.2 / §17.3). Runs every wave tick (so an already-emitted
 * drain runs `drainTicksLeft` down even with no boss alive). No new RNG: every
 * rule is tick- or HP-threshold-driven.
 */
function bossTick(state: GameState): void {
  // An emitted Power Drain runs its window down regardless of boss state.
  if (state.drainTicksLeft > 0) state.drainTicksLeft -= 1;

  // Phase transitions resolve per living boss, after this tick's damage.
  // `advanceBossPhase` → `spawnBossAdds` pushes new adds onto `state.enemies`
  // mid-iteration; that is safe because every add is a non-`kernel` type and
  // is skipped by the `e.type !== 'kernel'` filter below.
  let boss: Enemy | null = null;
  for (const e of state.enemies) {
    if (!e.alive || e.type !== 'kernel' || e.hp <= 0) continue;
    if (boss === null) boss = e;
    advanceBossPhase(state, e);
  }
  if (!boss) return;

  // Power-Drain charge cycle. An EMP on the boss suppresses its special
  // action: the cycle freezes entirely while the boss is stunned (§17.3); a
  // drain already emitted keeps running down (handled above).
  if (boss.statuses.emp) return;
  if (state.drainCharge > 0) {
    state.drainCharge -= 1;
    // Charge complete — emit a fresh `bossDrainDuration`-tick drain window.
    if (state.drainCharge === 0) state.drainTicksLeft = CONFIG.bossDrainDuration;
  } else {
    state.drainCooldown -= 1;
    if (state.drainCooldown <= 0) {
      state.drainCharge = CONFIG.bossDrainTelegraph;
      state.drainCooldown =
        CONFIG.bossDrainInterval[boss.phase - 1] ?? CONFIG.bossDrainInterval[0];
    }
  }
}

// --- wave director -----------------------------------------------------------

/** Advance the wave director: spawn at most one enemy this tick. */
function spawnTick(state: GameState): void {
  const wave = state.activeWave;
  const dir = state.director;
  if (dir.done) return;
  if (dir.cooldown > 0) {
    dir.cooldown -= 1;
    return;
  }
  const group = wave.groups[dir.groupIndex];
  if (!group) {
    dir.done = true;
    return;
  }
  // Effective HP folds the difficulty multiplier and the Overload tier together.
  const hpMult = diff(state).enemyHpMult * overloadTier(state).hpMult;
  // A fresh spawn walks the current global route (the fixed S-path, or the
  // live A* maze route — §16.6). The Kernel boss group spawns the boss (§17).
  if (group.type === 'kernel') {
    spawnKernel(state, hpMult, state.path);
  } else {
    state.enemies.push(createEnemy(state.nextId++, group.type, state.wave, hpMult, state.path));
  }
  dir.spawnedInGroup += 1;
  // The spawn that just happened consumed this tick, so wait `interval - 1`
  // further ticks — that makes the gap between spawns exactly `interval`.
  dir.cooldown = Math.max(0, group.interval - 1);
  if (dir.spawnedInGroup >= group.count) {
    dir.groupIndex += 1;
    dir.spawnedInGroup = 0;
    if (dir.groupIndex >= wave.groups.length) dir.done = true;
  }
}

/** Move every enemy along the path; leak damage when they reach the Core. */
function moveEnemies(state: GameState, sfx: Sfx): void {
  const perTick = 1 / CONFIG.tickRate;
  // Effective speed folds difficulty, the late-wave speed ramp and the
  // Overload tier together — see docs/GAME_DESIGN.md §8.
  const speedMult =
    diff(state).enemySpeedMult * waveSpeedRamp(state.wave) * overloadTier(state).speedMult;
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    expireStatuses(enemy, state.tick);
    enemy.prevPathDist = enemy.pathDist;
    // Streak lone-wolf sprint (docs/ENEMY_DESIGN.md §3.2 (b)). A pure-
    // deterministic per-tick proximity check: when no other living enemy sits
    // within `streakSprintRadius` of the Streak, its tick's effectiveSpeed is
    // multiplied by `streakSprintMult` (1.5×). Two Streaks running
    // shoulder-to-shoulder are *not* isolated; one that has out-paced its
    // escort *is*. `effectiveSpeed` already returns 0 when EMP'd, so an EMP
    // Burst hard-stops a sprinting Streak regardless of isolation. The
    // result is mirrored to `enemy.isSprinting` so render + tests can read it.
    let speed = effectiveSpeed(enemy, speedMult);
    if (enemy.type === 'streak') {
      const sprinting = speed > 0 && isStreakIsolated(state, enemy);
      enemy.isSprinting = sprinting;
      if (sprinting) speed *= CONFIG.streakSprintMult;
    }
    enemy.pathDist += speed * perTick;
    if (enemy.hitFlash > 0) enemy.hitFlash -= 1;
    // `distToCore` / `tile` track the enemy's own route every tick (§16.6).
    refreshEnemyTrack(enemy);
    if (enemy.pathDist >= enemy.path.length - 1) {
      enemy.alive = false;
      state.lives -= enemy.lifeDmg;
      sfx.play('life-lost');
      if (state.lives <= 0) {
        state.lives = 0;
        endRun(state, false, sfx);
        // The run is over: stop draining lives for the rest of this tick.
        return;
      }
    }
  }
}

/**
 * Sprint 31 — true when `streak` has no other living enemy within
 * `streakSprintRadius × tileSize` pixels (Manhattan ⇒ Euclidean — the spec's
 * geometry uses the live pixel point, which the chain-hop pattern in
 * `nearestUnhit` already does in pixel space). Pure deterministic per-tick
 * proximity check (no new RNG): integer-in, integer-out over the live enemy
 * set. The Kernel boss + its phase-spawned adds *count* as neighbours, by
 * design — a Streak running alongside the boss is not isolated.
 *
 * Determinism note (`docs/ENEMY_DESIGN.md` §7.7): Maze-mode mid-wave
 * re-pathing preserves `pathDist` and `tile`, and `enemyPoint` is derived
 * from those, so this returns the same value before and after a re-path
 * given the same enemy set.
 */
function isStreakIsolated(state: GameState, streak: Enemy): boolean {
  const radiusPx = CONFIG.streakSprintRadius * CONFIG.grid.tileSize;
  const radiusSq = radiusPx * radiusPx;
  const self = enemyPoint(streak);
  for (const other of state.enemies) {
    if (other === streak) continue;
    if (!other.alive) continue;
    const op = enemyPoint(other);
    if (dist2(self.x, self.y, op.x, op.y) <= radiusSq) return false;
  }
  return true;
}

// --- tower firing ------------------------------------------------------------

/** Squared pixel distance between two points. */
function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * Live playfield-local pixel position of an enemy, interpolated along its own
 * route. The shared accessor for towers, abilities, healing and rendering — so
 * a fixed-path and a Maze-mode enemy are handled by exactly the same code.
 */
function enemyPoint(enemy: Enemy): Point {
  return pointAtPath(enemy.path, enemy.pathDist);
}

/**
 * Order comparator for a targeting `mode`: < 0 when `a` is the better target
 * than `b`. Every mode has a fully deterministic tiebreak chain so a fixed
 * enemy set always resolves to one primary — see docs/GAME_DESIGN.md §5.
 */
function compareTargets(
  mode: TargetMode,
  a: Enemy,
  b: Enemy,
  distSq: Map<number, number>,
): number {
  // Ordering keys off `distToCore` (remaining tiles to the Core), so "closest
  // to Core" is correct on a fixed path and on every per-enemy maze route
  // alike — see docs/GAME_DESIGN.md §5.
  switch (mode) {
    case 'first':
      return a.distToCore - b.distToCore || a.id - b.id;
    case 'last':
      return b.distToCore - a.distToCore || a.id - b.id;
    case 'strongest':
      return b.hp + b.shield - (a.hp + a.shield) || a.distToCore - b.distToCore || a.id - b.id;
    case 'weakest':
      return a.hp + a.shield - (b.hp + b.shield) || a.distToCore - b.distToCore || a.id - b.id;
    case 'nearest':
      return (distSq.get(a.id) ?? 0) - (distSq.get(b.id) ?? 0) || a.id - b.id;
  }
}

/**
 * Living, targetable enemies for `tower`, ordered best-first by its
 * `targetMode`. Element 0 is the primary target; AoE towers (Cryo) slice the
 * ordered rest. An enemy is targetable when its tower→enemy distance is `≤
 * range` and `≥ minRange` — the Splash Mortar carries a non-zero `minRange`
 * dead zone (docs/GAME_DESIGN.md §5) it cannot fire into; every other tower
 * has `minRange` 0, so the inner-radius test is a no-op for them.
 */
function targetsInRange(state: GameState, tower: Tower): Enemy[] {
  const center = cellCenter(tower.col, tower.row);
  const rangeSq = (tower.range * CONFIG.grid.tileSize) ** 2;
  // The Mortar's inner dead zone — squared so it compares against `dist2`.
  // 0 for every non-Mortar tower, so `d < minRangeSq` can never exclude one.
  const minRangeSq = tower.minRange > 0 ? (tower.minRange * CONFIG.grid.tileSize) ** 2 : 0;
  // Sprint 34 — puzzle-mode line-of-sight filter (docs/PUZZLE_DESIGN.md §2).
  // `state.losWalls` is `null` outside puzzle mode, so the whole branch
  // collapses to a single null-check on Campaign / Endless / Maze — byte-
  // identical behaviour. **Mortar is exempt** (§2.4): its parabolic arc is
  // the indirect-fire payoff for the Sprint-14 min-range mechanic, so a wall
  // tile must never gate a Mortar target — `los.ts` makes the *caller* own
  // that decision, and we do it right here.
  const losWalls = state.losWalls;
  const applyLos = losWalls !== null && tower.type !== 'mortar';
  const list: Enemy[] = [];
  const distSq = new Map<number, number>();
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp <= 0) continue;
    const p = enemyPoint(enemy);
    const d = dist2(center.x, center.y, p.x, p.y);
    if (d > rangeSq) continue;
    // Inside the dead zone — the Mortar's arc cannot reach this enemy.
    if (d < minRangeSq) continue;
    // Direct-fire LOS gate — a wall tile between the tower centre and the
    // enemy's current pixel position cancels the candidacy. Cheap O(diagonal)
    // grid-DDA; allocation-free. Endpoint tiles are excluded by `losBlocked`,
    // so a tower adjacent to a wall still fires past it normally.
    if (applyLos && losBlocked(losWalls, center, p, CONFIG.grid.tileSize, CONFIG.grid.cols, CONFIG.grid.rows)) continue;
    list.push(enemy);
    distSq.set(enemy.id, d);
  }
  list.sort((a, b) => compareTargets(tower.targetMode, a, b, distSq));
  return list;
}

/** Pick a tower's primary target per its `targetMode`, or null if none in range. */
function selectTarget(state: GameState, tower: Tower): Enemy | null {
  return targetsInRange(state, tower)[0] ?? null;
}

/**
 * Deal one instance of `amount` damage to `enemy`. Unless `piercing`, the hit
 * is reduced by the enemy's effective armor (never below `minDamage`); a clank
 * cues when armor absorbs part of it. A Buffer's `shield` soaks damage before
 * `hp`, with spillover — a hit larger than the shield zeroes it and the
 * remainder carries straight through. Any hit restarts the shield-regen delay.
 *
 * The Cryo Emitter's **brittle** mark applies **here**, before armor / shield
 * resolution: while active, the raw `amount` is scaled by the mark's `amp` —
 * so the uplift carries uniformly through every damage path (single-target
 * shot, Arc chain link, Mortar blast, Airstrike, pierce lance, shield-soaked,
 * boss). The whole-system shape: every damage path funnels through here.
 */
function damage(enemy: Enemy, amount: number, piercing: boolean, sfx: Sfx): void {
  // Brittle is a pre-armor multiplier on the incoming damage — see the
  // Cryo bullet in §5. `Math.round` keeps the simulation in integer damage,
  // matching every other multiplier (the Arc surplus curve etc.).
  const amped = enemy.statuses.brittle
    ? Math.max(amount, Math.round(amount * enemy.statuses.brittle.amp))
    : amount;
  const dealt = piercing
    ? Math.max(CONFIG.minDamage, amped)
    : Math.max(CONFIG.minDamage, amped - enemy.armor);
  if (!piercing && enemy.armor > 0 && dealt < amped) sfx.play('armor-hit');
  enemy.hitFlash = CONFIG.hitFlashTicks;

  if (enemy.shield > 0) {
    const absorbed = Math.min(enemy.shield, dealt);
    enemy.shield -= absorbed;
    sfx.play(enemy.shield <= 0 ? 'shield-break' : 'shield-hit');
    const spill = dealt - absorbed;
    if (spill > 0) enemy.hp -= spill;
  } else {
    enemy.hp -= dealt;
  }
  // Any damage instance — even one fully absorbed — restarts a Buffer's
  // shield-regen delay. Inert on types with no shield (delay 0); the Kernel
  // boss carries no shield, so the delay is simply 0.
  enemy.shieldRegenCooldown =
    enemy.type === 'kernel' ? 0 : CONFIG.enemies[enemy.type].shieldRegenDelay;
}

/** Spawn a short-lived beam effect from `from` to `to`. */
function addBeam(
  state: GameState,
  from: { x: number; y: number },
  to: { x: number; y: number },
  kind: ProjectileKind,
): void {
  state.projectiles.push({
    id: state.nextId++,
    from: { x: from.x, y: from.y },
    to: { x: to.x, y: to.y },
    ttlTicks: CONFIG.beamTtlTicks,
    kind,
  });
}

/** Find the nearest not-yet-hit enemy within `maxPx` of `origin`. */
function nearestUnhit(
  state: GameState,
  origin: { x: number; y: number },
  hit: Enemy[],
  maxPx: number,
): Enemy | null {
  const maxSq = maxPx * maxPx;
  let best: Enemy | null = null;
  let bestSq = Infinity;
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp <= 0 || hit.includes(enemy)) continue;
    const p = enemyPoint(enemy);
    const d = dist2(origin.x, origin.y, p.x, p.y);
    if (d > maxSq) continue;
    if (best === null || d < bestSq || (d === bestSq && enemy.id < best.id)) {
      best = enemy;
      bestSq = d;
    }
  }
  return best;
}

/**
 * Set a tower's cooldown after firing; a brownout lengthens it. The Overcharge
 * ability (§15) drives this both ways:
 *
 *  - During the **burst window** (`state.overchargeTicksLeft > 0`) the
 *    cooldown-scaling efficiency is *floored* at the ability's boosted
 *    `efficiency` (1.6), so every tower fires faster and any brownout is
 *    lifted.
 *  - During the **aftershock brownout** (`state.aftershockTicksLeft > 0`,
 *    which fires the moment the burst window ends) the efficiency is
 *    *capped* at `aftershockEfficiency` (0.55) — every tower fires slower
 *    than normal for the aftershock window. This stacks multiplicatively
 *    with the Kernel's Power Drain (`bossDrainMult`, already folded into
 *    `state.power.efficiency` in `recomputePower`) by virtue of being a
 *    *cap* rather than a replacement.
 *
 * The two windows never overlap (the aftershock starts on the same tick the
 * burst ends — see `tickAbilities`), so the floor and cap order is safe.
 */
function applyCooldown(state: GameState, tower: Tower): void {
  // Guard the divisor: a mutated config (e.g. zero reactor power) could drive
  // efficiency to 0, which would otherwise yield an Infinite cooldown.
  let efficiency = Math.max(state.power.efficiency, CONFIG.minPowerEfficiency);
  if (state.overchargeTicksLeft > 0) {
    efficiency = Math.max(efficiency, abilityDef('overcharge').efficiency);
  } else if (state.aftershockTicksLeft > 0) {
    const cap = abilityDef('overcharge').aftershockEfficiency;
    if (cap > 0) efficiency = Math.min(efficiency, cap);
  }
  // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.2): Overclock divides this
  // tower's *own* cooldown — a per-tower multiplier applied AFTER the
  // grid-wide efficiency / Overcharge math, so the demand spike its draw
  // causes still bites every *other* tower normally. The `Math.max(1, …)`
  // floor matches Sprint 15's defensive discipline.
  const rateMult = tower.overclock ? CONFIG.overclockRateMult : 1;
  // Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B): the Pylon-aura cache,
  // recomputed by `applyPylonAuras` on every Pylon-roster change. `1` (inert)
  // on a tower with no Pylons in radius — so the OFF-aura path is byte-for-
  // byte the Sprint-26 divisor. The aura multiplies into `rateMult` *with*
  // Overclock: a saturated tower in OC fires at `1 + n × pylonAuraRateBuff`
  // × `overclockRateMult` (e.g. 1.20 × 1.6 = 1.92×), and that compound spike
  // still divides the same cooldown the Sprint-15 grid / Overcharge / boss-
  // drain math leaves us. Overclock's extra draw is the price for both.
  const auraMult = tower.auraRateMult;
  tower.cooldownLeft = Math.max(
    1,
    Math.ceil(tower.cooldownTicks / (efficiency * rateMult * auraMult)),
  );
}

/**
 * Cryo Emitter pulse — the **chokepoint painter** (docs/GAME_DESIGN.md §5).
 * A tight-radius AoE pulse: chip damage + slow + brittle damage-vulnerability
 * mark applied to every enemy in the (small) range. The tower's `targetMode`
 * is the sort key for the in-range list; the pulse then hits the first
 * `aoeTargets` of that order — a small cap by design (3 / 4 / 5), so a single
 * Cryo cannot drape brittle across a whole crescendo. Both slow and brittle
 * are *refresh-on-reapply* (strongest factor wins) — overlapping Cryos cannot
 * stack their way back to the pre-re-role board-wide multiplier.
 */
function fireCryo(state: GameState, tower: Tower, sfx: Sfx): void {
  const stats = CONFIG.towers.cryo.levels[tower.level - 1]!;
  const center = cellCenter(tower.col, tower.row);

  const inRange = targetsInRange(state, tower);
  const primary = inRange[0];
  if (!primary) {
    tower.targetId = -1;
    return;
  }
  tower.targetId = primary.id;
  const hit = inRange.slice(0, stats.aoeTargets);
  for (const enemy of hit) {
    damage(enemy, tower.damage, false, sfx);
    applySlow(enemy, stats.slowFactor, stats.slowDuration, state.tick);
    applyBrittle(enemy, stats.brittleAmp, stats.brittleDuration, state.tick);
    // Sprint 51 (polish-s51, docs/AUDIO_DESIGN.md additional cues): a short
    // shimmer-crack the moment the brittle mark lands. The audio module
    // throttles per-`enemy.id` so an enemy held in a chokepoint by an
    // overlapping Cryo does not machine-gun the cue. Falls back silently when
    // the sink is a bare `Sfx` mock (the puzzle evaluator's `SILENT_SFX`).
    sfx.playCryoBrittle?.(enemy.id, state.tick);
    addBeam(state, center, enemyPoint(enemy), 'cryo');
  }
  sfx.play('fire-cryo');
  applyCooldown(state, tower);
}

/**
 * Splash Mortar — a minimum-range indirect-fire emplacement (docs/GAME_DESIGN.md
 * §5). It picks a target per its `targetMode` from the firing annulus only —
 * `selectTarget` → `targetsInRange` already skips every enemy inside the
 * `minRange` dead zone — then lobs a shell that detonates at that enemy's
 * position, dealing uniform blast damage + 1 Corrosion stack to every enemy
 * within `blastRadius` tiles of the detonation. The blast itself is NOT
 * gated by the dead zone: a shell landing on the inner annulus edge can still
 * splash a little way inward — the dead zone constrains where a shell can be
 * aimed, not the explosion. With no enemy in the annulus the Mortar holds fire.
 */
function fireMortar(state: GameState, tower: Tower, sfx: Sfx): void {
  const target = selectTarget(state, tower);
  if (!target) {
    tower.targetId = -1;
    // Sprint 35 (audio-int-s35, docs/AUDIO_DESIGN.md §2.3 cue 2): the dead-
    // zone reject click. The Mortar's cooldown is ready (we are inside
    // `fireMortar`, which `fireTowers` calls only when `cooldownLeft <= 0`)
    // and `selectTarget` found no enemy in the firing annulus. If at least
    // one enemy lies INSIDE the inner `minRange` dead-zone the held shot is
    // a teachable moment — the audio module fires a single quiet click,
    // throttled per-`tower.id` (~30 ticks) so a cluster sitting in the
    // dead-zone cannot machine-gun the cue. Skipped silently for non-Mortar
    // weapons (they have `minRange === 0` so the loop would never trip
    // anyway) and for the bare `Sfx` mock used by the headless puzzle
    // evaluator (`SILENT_SFX`), where `playMortarDeadzone` is undefined.
    if (sfx.playMortarDeadzone && tower.minRange > 0) {
      const center = cellCenter(tower.col, tower.row);
      const minRangePx = tower.minRange * CONFIG.grid.tileSize;
      const minRangeSq = minRangePx * minRangePx;
      for (const enemy of state.enemies) {
        if (!enemy.alive || enemy.hp <= 0) continue;
        const p = enemyPoint(enemy);
        if (dist2(center.x, center.y, p.x, p.y) < minRangeSq) {
          sfx.playMortarDeadzone(tower.id, state.tick);
          break;
        }
      }
    }
    return;
  }
  tower.targetId = target.id;
  const center = cellCenter(tower.col, tower.row);
  const deton = enemyPoint(target);
  const blastPx = tower.blastRadius * CONFIG.grid.tileSize;
  const blastSq = blastPx * blastPx;
  addBeam(state, center, deton, 'mortar');
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp <= 0) continue;
    const p = enemyPoint(enemy);
    if (dist2(deton.x, deton.y, p.x, p.y) > blastSq) continue;
    damage(enemy, tower.damage, false, sfx);
    applyCorrode(enemy, state.tick);
  }
  sfx.play('fire-mortar');
  sfx.play('corrode');
  applyCooldown(state, tower);
}

/**
 * Sniper Rail — a line-of-fire pierce lance (docs/GAME_DESIGN.md §5). It picks
 * a primary target per its `targetMode`, then fires a straight beam from the
 * tower out through that target and damages EVERY living enemy standing on
 * that line: ahead of the tower, within range, and within `sniperBeamHalfWidth`
 * tiles of the line. The shot is armor-piercing — the lance punches through
 * armor. The firing line is a pure geometric function of the tower's placement
 * and this tick's enemy positions — no RNG, fully deterministic. Aligning the
 * tower with a long straight path run lines many enemies up on the lance; a
 * corner bends the path out of the line, so the lance then catches only a few.
 */
function fireSniper(state: GameState, tower: Tower, sfx: Sfx): void {
  const primary = selectTarget(state, tower);
  if (!primary) {
    tower.targetId = -1;
    return;
  }
  tower.targetId = primary.id;
  const center = cellCenter(tower.col, tower.row);
  const aim = enemyPoint(primary);
  // Unit direction of the firing line: tower → primary target.
  let dx = aim.x - center.x;
  let dy = aim.y - center.y;
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;
  const rangePx = tower.range * CONFIG.grid.tileSize;
  const halfWidthPx = CONFIG.sniperBeamHalfWidth * CONFIG.grid.tileSize;
  const piercing = CONFIG.towers.sniper.armorPiercing === true;
  // Sprint 34 — puzzle-mode line-of-fire clip (docs/PUZZLE_DESIGN.md §2.3).
  // The lance terminates at the first LOS wall tile along the firing line —
  // anything past the wall is shielded from the pierce, and the rendered
  // beam ends at the wall (visually the right cue: the lance dies on the
  // wall). We march DDA from `center` to a point one `rangePx` along the
  // unit direction; `losFirstHitT` returns the parametric distance to the
  // first wall hit, scaled to pixels by the `rangePx` segment length.
  // `Infinity` means no wall clip — the byte-identical Campaign / Endless /
  // Maze path. `state.losWalls === null` short-circuits inside `losFirstHitT`.
  let wallStopAlong = Infinity;
  if (state.losWalls !== null) {
    const probeEnd = { x: center.x + dx * rangePx, y: center.y + dy * rangePx };
    const tHit = losFirstHitT(
      state.losWalls,
      center,
      probeEnd,
      CONFIG.grid.tileSize,
      CONFIG.grid.cols,
      CONFIG.grid.rows,
    );
    if (tHit !== null) wallStopAlong = tHit * rangePx;
  }
  // Hit every living enemy on the lance, and track how far down the line the
  // last hit lands so the rendered beam can terminate on the final enemy.
  let farthest = len;
  // Sprint 35 (audio-int-s35, docs/AUDIO_DESIGN.md §2.2): count enemies the
  // lance actually pierced this shot — the same hit set the damage loop
  // resolves — so `playSniper` can pick its 1..5+ pitch tier from the live
  // alignment. Pure read of this tick's geometry; no RNG.
  let piercedCount = 0;
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp <= 0) continue;
    const p = enemyPoint(enemy);
    const ex = p.x - center.x;
    const ey = p.y - center.y;
    const along = ex * dx + ey * dy; // projection onto the firing line
    if (along < 0 || along > rangePx) continue; // behind the tower, or past range
    if (along > wallStopAlong) continue; // lance blocked by a LOS wall
    const perp = Math.abs(ex * dy - ey * dx); // perpendicular distance to the line
    if (perp > halfWidthPx) continue;
    damage(enemy, tower.damage, piercing, sfx);
    piercedCount += 1;
    if (along > farthest) farthest = along;
  }
  // The lance renders as one straight beam, tower → the last enemy it pierced
  // OR the wall it died on (whichever is closer). In a wall-clipped shot with
  // no enemies in front of the wall, `farthest` still equals `len` (the
  // primary target's distance) — but the primary itself was screened out of
  // `targetsInRange` by the LOS filter, so we never reach here in that case.
  const beamEnd = Math.min(farthest, wallStopAlong);
  addBeam(state, center, { x: center.x + dx * beamEnd, y: center.y + dy * beamEnd }, 'sniper');
  // Sprint 35 (audio-int-s35, docs/AUDIO_DESIGN.md §2.2): a great alignment
  // (high pierce count) should sound like a combo — the audio module clamps
  // onto the 1..5+ tier table. Falls back to the un-tiered `fire-sniper`
  // cue when the sink is a bare `Sfx` mock (e.g. the headless puzzle
  // evaluator's `SILENT_SFX`).
  if (sfx.playSniper) sfx.playSniper(piercedCount);
  else sfx.play('fire-sniper');
  applyCooldown(state, tower);
}

/** Resolve one tower's shot against the current enemy set. */
function fireTower(state: GameState, tower: Tower, sfx: Sfx): void {
  if (tower.type === 'cryo') {
    fireCryo(state, tower, sfx);
    return;
  }
  if (tower.type === 'mortar') {
    fireMortar(state, tower, sfx);
    return;
  }
  if (tower.type === 'sniper') {
    fireSniper(state, tower, sfx);
    return;
  }

  const target = selectTarget(state, tower);
  if (!target) {
    tower.targetId = -1;
    return;
  }
  tower.targetId = target.id;
  const center = cellCenter(tower.col, tower.row);

  if (tower.type === 'pulse') {
    // Pulse Turret — single-target hitscan beam.
    damage(target, tower.damage, false, sfx);
    addBeam(state, center, enemyPoint(target), 'pulse');
    sfx.play('fire-pulse');
  } else {
    // Arc Coil: chain from the primary target to nearby links. Both the chain
    // length and the per-shot damage couple to the live power-grid surplus
    // (docs/GAME_DESIGN.md §5) — an over-built grid charges the Arc up, a
    // brownout saps it. The chain length also scales with level; a Level-3
    // shot additionally EMPs the primary target.
    const surplus = state.power.surplus;
    const baseChain = CONFIG.towers.arc.levels[tower.level - 1]!.chainTargets;
    const chainTargets = baseChain + arcChainBonus(surplus);
    const arcDamage = Math.max(
      CONFIG.minDamage,
      Math.round(tower.damage * arcDamageMult(surplus)),
    );
    const hit: Enemy[] = [target];
    let prev = target;
    const jumpPx = CONFIG.arcChainRange * CONFIG.grid.tileSize;
    while (hit.length < chainTargets) {
      const next = nearestUnhit(state, enemyPoint(prev), hit, jumpPx);
      if (!next) break;
      hit.push(next);
      prev = next;
    }
    let from = center;
    for (const enemy of hit) {
      const to = enemyPoint(enemy);
      damage(enemy, arcDamage, false, sfx);
      addBeam(state, from, to, 'arc');
      from = to;
    }
    // Capstone perk: only the L3 Arc Coil applies an EMP, and only to the
    // chain's primary target — never the chain links.
    if (tower.level === CONFIG.maxTowerLevel) {
      applyEmp(target, state.tick);
      sfx.play('emp');
    }
    // Sprint 35 (audio-int-s35, docs/AUDIO_DESIGN.md §2.4 / REC 7): pitch the
    // fire cue across the four surplus bands so the player hears the same
    // grid texture the damage formula reads. The audio module owns the band
    // mapping; the call-site forwards the same `(surplus, freeband)` the
    // damage / chain coupling uses two lines up. Falls back to the
    // band-neutral `fire-arc` cue when the sink is a bare `Sfx` mock
    // (e.g. the headless puzzle evaluator) so behaviour stays audible there.
    if (sfx.playArc) sfx.playArc(surplus, CONFIG.arcSurplusFreeband);
    else sfx.play('fire-arc');
  }

  applyCooldown(state, tower);
}

/** Tick every weapon tower's cooldown and let ready ones fire. */
function fireTowers(state: GameState, sfx: Sfx): void {
  for (const tower of state.towers) {
    if (!CONFIG.towers[tower.type].weapon) continue;
    if (tower.cooldownLeft > 0) tower.cooldownLeft -= 1;
    if (tower.cooldownLeft <= 0) fireTower(state, tower, sfx);
  }
}

/**
 * Regenerate Buffer shields. Once a Buffer has waited out its post-damage
 * `shieldRegenCooldown`, it regains `shieldRegenRate` shield per tick up to
 * `maxShield`. An active EMP suppresses regeneration entirely.
 */
function regenShields(state: GameState): void {
  for (const enemy of state.enemies) {
    // Only the Buffer carries a shield — the Kernel boss has `maxShield` 0.
    if (!enemy.alive || enemy.maxShield <= 0 || enemy.type === 'kernel') continue;
    if (enemy.statuses.emp) continue;
    if (enemy.shieldRegenCooldown > 0) {
      enemy.shieldRegenCooldown -= 1;
      continue;
    }
    if (enemy.shield < enemy.maxShield) {
      const rate = CONFIG.enemies[enemy.type].shieldRegenRate;
      enemy.shield = Math.min(enemy.maxShield, enemy.shield + rate);
    }
  }
}

/**
 * Patch healers: every `patchHealInterval` ticks each Patch repairs all *other*
 * living enemies within `patchHealRadius` tiles by `patchHealAmount` HP (capped
 * at `maxHp`, no overheal, never itself). A deterministic action, not a status.
 */
function healTick(state: GameState, sfx: Sfx): void {
  if (state.tick % CONFIG.patchHealInterval !== 0) return;
  const radiusPx = CONFIG.patchHealRadius * CONFIG.grid.tileSize;
  const radiusSq = radiusPx * radiusPx;
  let healed = false;
  for (const patch of state.enemies) {
    if (!patch.alive || patch.type !== 'patch') continue;
    // An EMP'd Patch is stunned — it skips its repair pulse entirely.
    if (patch.statuses.emp) continue;
    const pp = enemyPoint(patch);
    for (const other of state.enemies) {
      if (other === patch || !other.alive || other.hp <= 0 || other.hp >= other.maxHp) continue;
      const op = enemyPoint(other);
      if (dist2(pp.x, pp.y, op.x, op.y) > radiusSq) continue;
      other.hp = Math.min(other.maxHp, other.hp + CONFIG.patchHealAmount);
      healed = true;
    }
  }
  if (healed) sfx.play('patch-heal');
}

// --- active abilities --------------------------------------------------------

/**
 * Detonate an Airstrike at tile (col, row): every enemy within the ability's
 * radius takes armour-piercing initial damage scaled by the wave's
 * `hpMultiplier`, then the tile becomes a **scorch zone** that lingers for
 * `scorchTicks` (docs/GAME_DESIGN.md §15). Each scorch tick is resolved in
 * `tickScorchZones` — chip damage + a brittle refresh on every enemy inside.
 * Damage routes shield-first on Buffers (the usual spillover) and applies no
 * status on the initial blast (the lingering zone is the status delivery).
 */
function fireAirstrike(
  state: GameState,
  col: number,
  row: number,
  def: AbilityDef,
  sfx: Sfx,
): void {
  const center = cellCenter(col, row);
  const dmg = Math.round(def.damage * hpMultiplier(state.wave));
  const radiusPx = def.radius * CONFIG.grid.tileSize;
  const radiusSq = radiusPx * radiusPx;
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp <= 0) continue;
    const p = enemyPoint(enemy);
    if (dist2(center.x, center.y, p.x, p.y) > radiusSq) continue;
    damage(enemy, dmg, true, sfx);
  }
  // Place the lingering scorch zone — captured `radius` so a later config
  // change cannot retroactively resize an in-flight zone. A zero scorchTicks
  // makes the ability behave like the pre-Sprint-20 one-off nuke (no zone).
  if (def.scorchTicks > 0) {
    state.scorchZones.push({ col, row, ticksLeft: def.scorchTicks, radius: def.radius });
  }
  // Visual: a shell arc dropping into the struck tile — tagged with its own
  // `'airstrike'` kind so the renderer never has to infer it from geometry.
  addBeam(state, { x: center.x, y: center.y - 220 }, center, 'airstrike');
  sfx.play('ability-airstrike');
  // Sprint 51 (polish-s51, docs/AUDIO_DESIGN.md §3.2 scorch residual): a short
  // sawtooth sizzle, internally delayed past the impact thud (the voice's
  // `delay: 0.4 s`), so the burn reads as the scorch zone settling in. Plays
  // once per Airstrike call regardless of how many enemies were hit. Skipped
  // entirely when there is no lingering scorch zone (a zero-scorchTicks config
  // disables the residual, parity with the original one-off nuke).
  if (def.scorchTicks > 0) sfx.play('airstrike-scorch');
}

/**
 * Advance every live Airstrike scorch zone by one tick (docs/GAME_DESIGN.md
 * §15). Each surviving zone re-damages every enemy inside it for
 * `scorchTickDamage × hpMultiplier(wave)` armour-piercing chip damage and
 * refreshes a brittle mark at `scorchBrittleAmp` for `scorchBrittleTicks` —
 * so a zone painted on a chokepoint amps every other tower's shots into it
 * for as long as the enemy stands in the burn. Zones with `ticksLeft === 0`
 * are dropped. Pure deterministic function of integer state — no RNG.
 */
function tickScorchZones(state: GameState, sfx: Sfx): void {
  if (state.scorchZones.length === 0) return;
  const def = abilityDef('airstrike');
  const tickDmg = Math.round(def.scorchTickDamage * hpMultiplier(state.wave));
  for (const zone of state.scorchZones) {
    const center = cellCenter(zone.col, zone.row);
    const rPx = zone.radius * CONFIG.grid.tileSize;
    const rSq = rPx * rPx;
    for (const enemy of state.enemies) {
      if (!enemy.alive || enemy.hp <= 0) continue;
      const p = enemyPoint(enemy);
      if (dist2(center.x, center.y, p.x, p.y) > rSq) continue;
      // Brittle first — `damage()` reads the mark to amp the chip damage too,
      // so the zone is consistent with every other damage source. Refresh-
      // on-reapply (max amp wins) so a stronger Cryo painter brittle is
      // preserved.
      if (def.scorchBrittleAmp > 1.0 && def.scorchBrittleTicks > 0) {
        applyBrittle(enemy, def.scorchBrittleAmp, def.scorchBrittleTicks, state.tick);
      }
      if (tickDmg > 0) damage(enemy, tickDmg, true, sfx);
    }
    zone.ticksLeft -= 1;
  }
  // Sprint 49 perf-wins P5 — in-place compaction (was: `state.scorchZones =
  // state.scorchZones.filter(...)`). Byte-equivalent (same items, same order).
  compactInPlace(state.scorchZones, (z) => z.ticksLeft > 0);
}

/**
 * Fire an EMP Burst (docs/GAME_DESIGN.md §15). The base effect stuns every
 * living enemy for `empTicks`; the Sprint 20 re-role *also* applies a field-
 * wide brittle mark at `empBrittleAmp` for the same window, and awards a
 * per-stunned-enemy **score bonus** scaled by the wave's Overload
 * `scoreMult` — the proactive Overload synergy. The bonus is folded directly
 * into `state.score` (never bountied through `bountyDecay`); the Kernel boss
 * is counted exactly once like any other stunned enemy.
 */
function fireEmpBurst(state: GameState, def: AbilityDef, sfx: Sfx): void {
  const scoreMult = overloadTier(state).scoreMult;
  const perEnemyBonus = Math.round(def.empScoreBonusPer * scoreMult);
  let stunned = 0;
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    applyEmp(enemy, state.tick, def.empTicks);
    if (def.empBrittleAmp > 1.0 && def.empTicks > 0) {
      applyBrittle(enemy, def.empBrittleAmp, def.empTicks, state.tick);
    }
    stunned += 1;
  }
  if (perEnemyBonus > 0 && stunned > 0) {
    state.score += perEnemyBonus * stunned;
  }
  sfx.play('ability-emp');
  // Sprint 51 (polish-s51, docs/AUDIO_DESIGN.md §3.3 synergy thump): when the
  // EMP fires while the Overcharge burst window is still open, layer in an
  // extra sub-bass thump so the combo *lands*. The base `ability-emp` voice
  // stays unchanged; this is purely additive reinforcement of the synergy the
  // mechanic already encodes (Overcharge → EMP combo). No sim branching.
  if (state.overchargeTicksLeft > 0) sfx.play('emp-synergy-thump');
}

/**
 * Trigger an active ability — see docs/GAME_DESIGN.md §15. Valid only in the
 * wave phase; fails if the ability is on cooldown, gold is short, or — for
 * `airstrike` — the target tile is missing / off-grid. On success it spends
 * the gold and starts the cooldown. Returns true on success.
 */
export function useAbility(
  state: GameState,
  id: AbilityId,
  sfx: Sfx,
  col?: number,
  row?: number,
): boolean {
  if (state.phase !== 'wave') return false;
  const ability = state.abilities.find((a) => a.id === id);
  if (!ability) return false;
  if (ability.cooldownLeft > 0) {
    notify(state, `${ability.name} recharging`);
    return false;
  }
  if (state.gold < ability.cost) {
    notify(state, 'Not enough gold');
    sfx.play('error');
    return false;
  }
  const def = abilityDef(id);
  switch (id) {
    case 'overcharge':
      // Re-firing restarts the burst window — and, if the previous fire's
      // aftershock is still ticking, clears it (the player paid 50 gold to
      // escape their own debt; usually wrong, but the door is open).
      state.overchargeTicksLeft = def.durationTicks;
      state.aftershockTicksLeft = 0;
      sfx.play('ability-overcharge');
      break;
    case 'airstrike':
      if (col === undefined || row === undefined || !inBounds(col, row)) {
        notify(state, 'Airstrike needs a target tile');
        return false;
      }
      fireAirstrike(state, col, row, def, sfx);
      break;
    case 'empBurst':
      fireEmpBurst(state, def, sfx);
      break;
  }
  state.gold -= ability.cost;
  ability.cooldownLeft = ability.cooldownTicks;
  ability.ready = false;
  notify(state, `${ability.name} engaged`);
  return true;
}

/**
 * Advance ability cooldowns, the Overcharge burst window and its aftershock
 * brownout by one tick. Called only from the wave phase, so cooldowns and
 * effect windows never tick down during the untimed build phase — they carry
 * across waves intact (see docs/GAME_DESIGN.md §15). When the burst window
 * runs out the aftershock window is queued for the configured length: the
 * proactive cost of the burst. Re-firing Overcharge during the aftershock
 * (handled in `useAbility`) re-enters the burst and clears the aftershock —
 * a 50-gold escape hatch, usually wrong.
 */
function tickAbilities(state: GameState, sfx: Sfx): void {
  for (const ability of state.abilities) {
    if (ability.cooldownLeft > 0) ability.cooldownLeft -= 1;
    ability.ready = ability.cooldownLeft <= 0;
  }
  if (state.overchargeTicksLeft > 0) {
    state.overchargeTicksLeft -= 1;
    // Burst just ended this tick → start the aftershock brownout. A 0-tick
    // config disables the cost (parity with the pre-Sprint-20 ability).
    if (state.overchargeTicksLeft === 0) {
      const aftershock = abilityDef('overcharge').aftershockTicks;
      if (aftershock > 0) state.aftershockTicksLeft = aftershock;
      // Sprint 51 (polish-s51, docs/AUDIO_DESIGN.md §3.1 close stinger): a
      // soft click-and-decay on the tick the burst window expires — tells
      // the player the window is closed without staring at the HUD timer.
      // Additive only: the open swell (`ability-overcharge`) still fires
      // from `useAbility`. Pure audio: no sim branching on the play.
      sfx.play('overcharge-end');
    }
  } else if (state.aftershockTicksLeft > 0) {
    state.aftershockTicksLeft -= 1;
  }
}

// --- deaths, particles, projectiles ------------------------------------------

/** Spawn a kill burst of particles at (x, y). */
function spawnParticles(state: GameState, x: number, y: number, color: string, rng: Rng): void {
  for (let i = 0; i < CONFIG.particlesPerKill; i += 1) {
    const angle = rng.next() * Math.PI * 2;
    const speed = rng.range(0.6, 2.6);
    state.particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      ttlTicks: CONFIG.particleTtlTicks,
      maxTtl: CONFIG.particleTtlTicks,
      color,
    });
  }
}

/**
 * Convert dead-but-not-leaked enemies into gold, score and particles. A Fork
 * killed by damage splits into Bit children at its `pathDist` (see §5); a Fork
 * that leaked is skipped here, so it never splits.
 */
function resolveDeaths(state: GameState, rng: Rng, sfx: Sfx): void {
  const children: Enemy[] = [];
  const d = diff(state);
  // Per-kill gold = bounty × difficulty × decay × Overload `goldMult`; per-kill
  // score = raw bounty × Overload `scoreMult` only (§8/§9) — never scaled by
  // `bountyMult` or `D(w)`, so score stays comparable across difficulties.
  const tier = overloadTier(state);
  const decay = bountyDecay(state.wave);
  const childHpMult = d.enemyHpMult * tier.hpMult;
  for (const enemy of state.enemies) {
    if (!enemy.alive || enemy.hp > 0) continue;
    enemy.alive = false;
    state.gold += Math.max(1, Math.floor(enemy.bounty * d.bountyMult * decay * tier.goldMult));
    state.score += Math.round(enemy.bounty * tier.scoreMult);
    const p = enemyPoint(enemy);
    spawnParticles(state, p.x, p.y, enemyColor(enemy.type), rng);
    sfx.play('death');

    if (enemy.type === 'fork') {
      // In Maze Mode a child A*-paths fresh from the Fork's tile (§16.6); on a
      // fixed path it inherits the Fork's route + `pathDist` (co-located).
      const { path: childPath, dist: childDist } = coLocatedSpawn(state, enemy);
      for (let i = 0; i < CONFIG.forkChildCount; i += 1) {
        const child = createEnemy(
          state.nextId++,
          'bit',
          state.wave,
          childHpMult,
          childPath,
          enemy.id,
        );
        child.pathDist = childDist;
        child.prevPathDist = childDist;
        refreshEnemyTrack(child);
        // Children inherit the Fork's active slow, brittle mark and EMP.
        if (enemy.statuses.slow) {
          child.statuses.slow = {
            factor: enemy.statuses.slow.factor,
            expiresTick: enemy.statuses.slow.expiresTick,
          };
        }
        if (enemy.statuses.brittle) {
          child.statuses.brittle = {
            amp: enemy.statuses.brittle.amp,
            expiresTick: enemy.statuses.brittle.expiresTick,
          };
        }
        if (enemy.statuses.emp) {
          child.statuses.emp = { expiresTick: enemy.statuses.emp.expiresTick };
        }
        children.push(child);
      }
    }
  }
  // Sprint 49 perf-wins P5 — in-place compaction (was: `state.enemies =
  // state.enemies.filter(e => e.alive)`). Byte-equivalent: same survivors,
  // same order. The Fork children appended below also keep the same insertion
  // pattern (`push(...children)` after the survivors).
  compactInPlace(state.enemies, (e) => e.alive);
  if (children.length > 0) state.enemies.push(...children);
}

/** Advance and cull kill particles. */
function updateParticles(state: GameState): void {
  for (const p of state.particles) {
    p.x += p.vx;
    p.y += p.vy;
    p.vx *= CONFIG.particleFriction;
    p.vy *= CONFIG.particleFriction;
    p.ttlTicks -= 1;
  }
  // Sprint 49 perf-wins P5 — in-place compaction. Byte-equivalent.
  compactInPlace(state.particles, (p) => p.ttlTicks > 0);
}

/** Age out expired beam effects. */
function updateProjectiles(state: GameState): void {
  for (const p of state.projectiles) p.ttlTicks -= 1;
  // Sprint 49 perf-wins P5 — in-place compaction. Byte-equivalent.
  compactInPlace(state.projectiles, (p) => p.ttlTicks > 0);
}

/** Award the wave-clear bonus and return to build, or end the run on the last wave. */
function checkWaveEnd(state: GameState, sfx: Sfx): void {
  if (state.phase !== 'wave') return;
  if (!state.director.done || state.enemies.length > 0) return;
  // The wave is over — clear any lingering Power Drain so the build-phase grid
  // readout is honest (the boss that emitted it is gone — §17.3).
  state.drainTicksLeft = 0;

  // Overload lifts every wave-clear award — wave-clear gold by `goldMult`,
  // wave-clear score by `scoreMult`; the wave-clear gold bonus is the recovery
  // lever, never touched by bounty decay (§9).
  const tier = overloadTier(state);
  state.score += Math.round(CONFIG.waveClearScore * tier.scoreMult);
  const bonus = Math.floor(
    (CONFIG.waveBonusBase + CONFIG.waveBonusPerWave * state.wave) * tier.goldMult,
  );
  state.gold += bonus;
  // Endless waves (beyond the campaign table) pay an extra depth bonus (score).
  if (state.wave > CONFIG.waves.length) {
    state.score += Math.round(
      CONFIG.endlessDepthBonus * (state.wave - CONFIG.waves.length) * tier.scoreMult,
    );
  }
  // Maze Mode: a wave-clear bonus scaling with how far the player has stretched
  // the route past the ~19-segment gauntlet baseline — primarily score, plus a
  // modest gold trickle. A long winding maze wastes towers as dead wall, so
  // without this it is a pure efficiency loss; the bonus turns route length
  // into a genuine score-vs-safety trade (docs/GAME_DESIGN.md §16.7). Both
  // dials ride the Overload reward multipliers, like every wave-clear award.
  let mazeBonus = 0;
  if (state.mode === 'maze') {
    const extra = Math.max(0, state.mazePathLength - CONFIG.mazeBaselineLength);
    state.score += Math.round(extra * CONFIG.mazeBonusScorePerTile * tier.scoreMult);
    mazeBonus = Math.floor(extra * CONFIG.mazeBonusGoldPerTile * tier.goldMult);
    state.gold += mazeBonus;
  }

  // Campaign and Maze win by clearing the final table wave (wave 10); Endless
  // never wins, and a Maze run continued into Endless (wave 11+) does not
  // re-trigger the win — the `=== waves.length` check excludes it (§16.1).
  if (
    (state.mode === 'campaign' || state.mode === 'maze') &&
    state.wave === CONFIG.waves.length
  ) {
    endRun(state, true, sfx);
  } else {
    state.phase = 'build';
    sfx.play('wave-clear');
    notify(state, `Wave ${state.wave} cleared  +${bonus + mazeBonus}g`);
  }
}

/**
 * Advance the whole simulation by exactly one logical tick. Deterministic for
 * a fixed seed + identical inputs — this is the backbone of the test contract.
 */
export function simTick(state: GameState, rng: Rng, sfx: Sfx): void {
  state.tick += 1;

  if (state.notice) {
    state.notice.ttl -= 1;
    if (state.notice.ttl <= 0) state.notice = null;
  }
  updateParticles(state);
  updateProjectiles(state);
  recomputePower(state);

  if (state.phase !== 'wave') return;

  spawnTick(state);
  moveEnemies(state, sfx);
  // A Core breach in moveEnemies ends the run mid-tick — towers and deaths
  // must not keep resolving against an already-lost run.
  if (state.phase !== 'wave') return;
  regenShields(state);
  healTick(state, sfx);
  fireTowers(state, sfx);
  // Airstrike scorch zones tick after towers so chip damage and brittle
  // refreshes land on enemies that survived this tick's tower volley — the
  // brittle mark amps the *next* tick's tower shots, not this one (a one-tick
  // priming delay that keeps the order intuitive). See docs/GAME_DESIGN.md §15.
  tickScorchZones(state, sfx);
  // Boss phases + Power Drain resolve after this tick's tower damage (§17).
  bossTick(state);
  resolveDeaths(state, rng, sfx);
  // Ability cooldowns + the Overcharge burst/aftershock windows advance only
  // inside the wave (build phase is untimed — §15).
  tickAbilities(state, sfx);
  checkWaveEnd(state, sfx);
}
